import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  AfterViewInit, ApplicationRef, Component, DestroyRef, ElementRef, OnDestroy, OnInit,
  computed, effect, inject, signal, untracked, viewChild,
} from '@angular/core';
import { AbstractControl, FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiError } from '../../../core/auth/auth.models';
import { AuthService } from '../../../core/auth/auth.service';
import {
  INTEGRATED_SYSTEMS, SCOPE_SUB_SCOPES, complaintsAllowed, naturesFor, originDetailsFor,
  originsFor, typesFor,
} from '../../../core/occurrence/occurrence-catalog';
import {
  complaintReasonOptions,
  HEALTH_DATA_CATEGORIES,
  HEALTH_DATA_COMPLAINT_REASON,
  HEALTH_DATA_NOTE,
  IMPACT_OPTIONS,
  INCIDENT_NATURE_OPTIONS,
  INTEGRATED_SYSTEM_LABELS,
  INTERESTED_PARTY_OPTIONS,
  NATURE_LABELS,
  ORGANIZATION_RELATION_OPTIONS,
  PROCESS_AREA_OPTIONS,
  reportCategoryOptions,
  SITUATION_OPTIONS,
  STATE_LABELS,
  STATE_OPTIONS,
  START_LABELS,
  STATE_ORDER,
  OUTCOME_OPTIONS,
  CLOSING_TYPE_OPTIONS,
  CLOSING_TYPE_LABELS,
  TRIAGE_RESULT_OPTIONS,
  WORKER_TYPE_OPTIONS,
  ORIGIN_DETAILS_WITH_OTHER,
  ORIGIN_DETAIL_LABELS,
  ORIGIN_LABELS,
  ORIGINS_WITH_WHO,
  PARTICIPANT_OPTIONS,
  SCOPE_OPTIONS,
  SUB_SCOPE_LABELS,
  TYPE_LABELS,
} from '../../../core/occurrence/occurrence.labels';
import {
  ClosingType,
  ComplaintReason,
  EffectivenessEvaluation,
  IncidentNature,
  IntegratedSystem,
  InterestedParty,
  Occurrence,
  OccurrenceImpact,
  OccurrenceNature,
  OccurrenceOrigin,
  OccurrenceOriginDetail,
  OccurrenceOutcome,
  OccurrenceScope,
  OccurrenceSituation,
  OccurrenceState,
  OccurrenceSubScope,
  OccurrenceType,
  OrganizationRelation,
  ParticipantIdentification,
  ProcessArea,
  ReportCategory,
  TriageResult,
  WorkerType,
} from '../../../core/occurrence/occurrence.models';
import { OccurrenceService } from '../../../core/occurrence/occurrence.service';
import { CriticalCaseService } from '../../../core/critical-case/critical-case.service';
import { CriticalCaseDetail } from '../../../core/critical-case/critical-case.models';
import { User } from '../../../core/user/user.models';
import { UserService } from '../../../core/user/user.service';
import { ToastService } from '../../../core/ui/toast.service';
import { AutoHeight } from '../../../shared/auto-height/auto-height';
import { FormStep, FormStepState, FormSteps } from '../../../shared/form-steps/form-steps';
import { OccurrenceActions } from '../../../shared/occurrence-actions/occurrence-actions';
import { OccurrenceFollowUps } from '../../../shared/occurrence-follow-ups/occurrence-follow-ups';
import { OccurrenceEvidences } from '../../../shared/occurrence-evidences/occurrence-evidences';
import { ConfirmDialog } from '../../../shared/confirm-dialog/confirm-dialog';
import { ClientPicker } from '../../../shared/client-picker/client-picker';
import { DatePicker } from '../../../shared/date-picker/date-picker';

/**
 * Duração e curva do voo entre o meio do ecrã e o canto.
 *
 * <p>A curva acelera e trava de forma equilibrada. Uma curva muito adiantada percorre quase todo
 * o caminho na primeira metade do tempo — boa para um fade curto, mas num percurso longo lê-se
 * como um estalo seguido de um ajuste, e não como uma janela a encolher.
 */
const DOCK_ANIMATION_MS = 340;
const DOCK_EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';

function isoToday(): string {
  const now = new Date();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${mm}-${dd}`;
}

/** Liga/desliga o `required` sem apagar os validadores que o controlo já tinha. */
/** Um campo conta como preenchido quando tem algo que não seja o vazio de origem. */
function hasValue(value: unknown): boolean {
  if (value === null || value === undefined || value === false) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return true;
}

function setRequired(control: AbstractControl, required: boolean): void {
  const has = control.hasValidator(Validators.required);
  if (required === has) return;
  if (required) control.addValidators(Validators.required);
  else control.removeValidators(Validators.required);
  control.updateValueAndValidity({ emitEvent: false });
}

function addDays(dateStr: string, days: number): string {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(year, month - 1, day + days);
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function addMonths(dateStr: string, months: number): string {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(year, month - 1 + months, day);
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/** Motivos de reclamação que obrigam a identificar quem reclama (espelha o backend). */
const REASONS_REQUIRING_IDENTIFICATION: ComplaintReason[] = ['PRODUTO_SERVICO', 'ATENDIMENTO_COMUNICACAO'];

@Component({
  selector: 'app-occurrence-form',
  imports: [
    ReactiveFormsModule, FormsModule, DatePipe, DatePicker, ClientPicker, ConfirmDialog,
    FormSteps, AutoHeight,
    OccurrenceFollowUps, OccurrenceActions, OccurrenceEvidences,
  ],
  templateUrl: './occurrence-form.html',
})
export class OccurrenceForm implements OnInit, AfterViewInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly service = inject(OccurrenceService);
  private readonly users = inject(UserService);
  private readonly criticalCases = inject(CriticalCaseService);
  private readonly auth = inject(AuthService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly toasts = inject(ToastService);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly dialog = viewChild<ElementRef<HTMLDialogElement>>('dialog');
  private readonly surface = viewChild<ElementRef<HTMLElement>>('surface');
  private readonly autoHeight = viewChild(AutoHeight);
  private readonly appRef = inject(ApplicationRef);

  protected readonly scopeOptions = SCOPE_OPTIONS;
  /**
   * As duas opções aparecem sempre; quando o anónimo não se admite, fica à vista desativado e com
   * o motivo ao lado. No exercício de direitos era retirado da lista, e noutros casos desativado
   * — a revisão de 2026-10-15 pediu o desativado em todos.
   */
  protected readonly participantOptions = PARTICIPANT_OPTIONS;

  /**
   * Porque é que o anónimo não se pode escolher agora, ou null quando se pode. Só o site admite
   * anónimo (documento de 2026-10-05), e o site regista sozinho — por isso, à mão, nunca.
   */
  protected readonly anonymousBlockedReason = computed<string | null>(() => {
    if (this.showDataSubject()) return 'não permitido no exercício de direitos';
    if (this.identificationLocked()) return 'este motivo obriga a participação identificada';
    if (this.origin() !== 'SITE') return 'só as participações pelo site podem ser anónimas';
    return null;
  });
  protected readonly incidentNatureOptions = INCIDENT_NATURE_OPTIONS;
  protected readonly natureLabels = NATURE_LABELS;
  protected readonly impactOptions = IMPACT_OPTIONS;
  protected readonly processAreaOptions = PROCESS_AREA_OPTIONS;
  /** Os motivos dependem do subâmbito: o bem-estar é da NP 4590, o produto da ISO 9001. */
  protected readonly reasonOptions = computed(() => complaintReasonOptions(this.subScope()));
  /** As categorias dependem do âmbito desde o documento de 2026-10-01: o RGPD tem a sua. */
  protected readonly categoryOptions = computed(() => reportCategoryOptions(this.scope()));
  protected readonly relationOptions = ORGANIZATION_RELATION_OPTIONS;
  protected readonly workerTypeOptions = WORKER_TYPE_OPTIONS;
  protected readonly interestedPartyOptions = INTERESTED_PARTY_OPTIONS;
  protected readonly stateOptions = STATE_OPTIONS;
  protected readonly stateLabels = STATE_LABELS;
  protected readonly situationOptions = SITUATION_OPTIONS;
  protected readonly triageResultOptions = TRIAGE_RESULT_OPTIONS;
  protected readonly outcomeOptions = OUTCOME_OPTIONS;
  /**
   * O tamanho da coluna do título (V33). Era 200, sem aviso no ecrã: passar dele chegava como
   * "operação conflita com dados existentes" (2026-10-16).
   */
  private static readonly TITLE_MAX_LENGTH = 500;
  protected readonly titleMaxLength = OccurrenceForm.TITLE_MAX_LENGTH;
  protected readonly closingTypeOptions = CLOSING_TYPE_OPTIONS;
  protected readonly closingTypeLabels = CLOSING_TYPE_LABELS;
  protected readonly healthDataNote = HEALTH_DATA_NOTE;
  protected readonly allSystems = INTEGRATED_SYSTEMS;
  protected readonly systemLabels = INTEGRATED_SYSTEM_LABELS;

  protected readonly id = signal<number | null>(null);
  protected readonly code = signal<string | null>(null);
  protected readonly loading = signal(false);
  /** Teto da data da ocorrência: o servidor recusa datas futuras, e o calendário não as oferece. */
  protected readonly today = isoToday();

  // --- Passos do formulário
  /**
   * Que campos vivem em cada passo. Serve para duas coisas: dizer quanto está preenchido, e
   * levar ao passo certo o campo que o servidor (ou a validação) recusou — num formulário
   * dividido em separadores, o campo errado pode estar fora do que se vê.
   *
   * <p>Seguimento e Evidências não têm campos: são históricos com entradas próprias, que só
   * existem depois de a ocorrência existir.
   */
  private static readonly STEP_FIELDS: Record<string, readonly string[]> = {
    identificacao: ['situation', 'state', 'closingType', 'closingResult', 'outcome', 'closingDate',
      'legalHold', 'legalHoldNote'],
    caracterizacao: ['scope', 'scopeOther', 'subScope', 'subScopeOther', 'type', 'typeOther',
      'nature', 'processArea', 'processAreaOther', 'complaintReason', 'complaintReasonOther',
      'reportCategoryOther', 'recurringComplaint', 'origin', 'originDetail', 'originOther',
      'interestedParty', 'parentSelect', 'participantIdentification', 'participantName',
      'participantEmail', 'participantPhone', 'participantConfidential', 'participantRelation',
      'participantWorkerType', 'participantClientId', 'targetedPersons', 'conflictParties'],
    descricao: ['title', 'description', 'occurrenceDate'],
    // O impacto e o requisito vieram da Avaliação, que deixou de existir (2026-10-03).
    analise: ['initialAnalysis', 'causeAnalysis', 'triageResult', 'triageDecidedAt',
      'triageDecisionDeadline', 'impact', 'requirementReference',
      // O prazo para avaliar a eficácia veio da Descrição (documento de 2026-10-08): é na
      // análise que se decide se há eficácia para verificar, e até quando.
      'effectivenessEvaluationDeadline',
      // A espera de elementos passou do Tratamento para cá em 2026-10-03: é na análise que se
      // percebe que falta alguma coisa para decidir.
      'awaitingElements', 'elementsRequestedAt', 'elementsResponseDeadline',
      // Exercício de direitos: a identidade validada passou da Caracterização para cá
      // (revisão de 2026-10-15), e os dados do titular deixaram de se pedir à parte.
      'dataSubjectValidated'],
    tratamento: ['actionDescription', 'actionResponsibleId', 'actionImplementationDeadline',
      'informDeadline'],
    monitorizacao: [],
    eficacia: ['effectivenessEvaluation', 'effectivenessEvaluationDate', 'effectivenessFindings',
      'effectivenessUnableReason'],
    evidencias: [],
  };

  protected readonly step = signal('caracterizacao');

  /**
   * Os passos visíveis. Identificação, Seguimento e Evidências só na edição: mudar o estado
   * de uma ocorrência que ainda não existe não quer dizer nada, e o seguimento e as evidências
   * precisam de uma ocorrência a que se agarrar.
   */
  protected readonly steps = computed<FormStep[]>(() => {
    const all: FormStep[] = [
      { key: 'identificacao', label: 'Identificação', icon: 'fe-hash' },
      { key: 'caracterizacao', label: 'Caracterização', icon: 'fe-tag' },
      { key: 'descricao', label: 'Descrição', icon: 'fe-file-text' },
      { key: 'analise', label: 'Análise', icon: 'fe-search' },
      { key: 'tratamento', label: 'Tratamento', icon: 'fe-zap' },
      { key: 'monitorizacao', label: 'Seguimento', icon: 'fe-activity' },
      { key: 'eficacia', label: 'Eficácia', icon: 'fe-check-circle' },
      { key: 'evidencias', label: 'Evidências', icon: 'fe-paperclip' },
    ];
    const onlyOnEdit = ['identificacao', 'monitorizacao', 'evidencias'];
    return this.isEdit() ? all : all.filter((s) => !onlyOnEdit.includes(s.key));
  });

  /**
   * O estado a que cada secção pertence — é dele que sai o botão "Iniciar…" no rodapé.
   *
   * <p>As secções que não estão aqui não pertencem a estado nenhum: a Caracterização e a
   * Descrição fazem-se no registo, a Identificação é onde se muda o estado à mão e as
   * Evidências são um histórico.
   *
   * <p>Houve aqui, até 2026-10-06, um tranque: a secção só abria depois de a ocorrência chegar
   * ao estado dela. Com o servidor a exigir a secção para deixar entrar no estado, ninguém saía
   * do registo. Agora todas as secções se percorrem e preenchem a qualquer momento — o que o
   * estado comanda é só o botão que a secção oferece.
   */
  private static readonly STEP_STATE: Record<string, OccurrenceState> = {
    analise: 'EM_ANALISE',
    tratamento: 'EM_TRATAMENTO',
    monitorizacao: 'EM_MONITORIZACAO',
    eficacia: 'AVALIACAO_REAVALIACAO',
  };

  /**
   * Estados a que não se chega por botão de arranque: o "Concluir tratamento" já lá põe a
   * ocorrência, e um "Iniciar seguimento" ao lado era um segundo caminho para o mesmo sítio
   * (documento de 2026-10-08).
   */
  private static readonly NO_START_BUTTON: readonly OccurrenceState[] = ['EM_MONITORIZACAO'];

  /**
   * Marca de cada passo. O vermelho ganha aos outros: um passo cheio mas com um campo recusado
   * tem de se ver como problema, não como concluído.
   */
  /**
   * Prazos que o sistema calcula ao registar (+6 e +8 meses da data da ocorrência). Nascem
   * preenchidos em quase todas, por isso não dizem nada sobre o que já foi escrito.
   */
  private static readonly AUTO_FILLED: readonly string[] = [
    'triageDecisionDeadline', 'informDeadline', 'actionImplementationDeadline',
    'effectivenessEvaluationDeadline', 'elementsResponseDeadline',
  ];

  protected readonly stepStates = computed<Record<string, FormStepState>>(() => {
    // Depende do estado do formulário, que não é um sinal — este contador força o recálculo.
    this.formTouch();
    const states: Record<string, FormStepState> = {};
    for (const step of this.steps()) {
      const fields = OccurrenceForm.STEP_FIELDS[step.key] ?? [];
      const controls = fields.map((name) => this.form.get(name)).filter((c) => c !== null);
      if (controls.some((control) => control!.invalid && control!.touched)) {
        states[step.key] = 'erro';
        continue;
      }
      // Os prazos que o sistema calcula sozinho não contam como secção começada: quase toda a
      // ocorrência nasce com eles, e o Tratamento e a Eficácia apareciam já preenchidos a quem
      // só tinha escrito a Descrição.
      const escritos = fields
        .filter((name) => !OccurrenceForm.AUTO_FILLED.includes(name))
        .map((name) => this.form.get(name))
        .filter((control) => control !== null);
      const filled = escritos.filter((control) => hasValue(control!.value)).length;
      if (escritos.length === 0 || filled === 0) {
        states[step.key] = 'vazio';
      } else {
        states[step.key] = filled === escritos.length ? 'completo' : 'parcial';
      }
    }
    return states;
  });

  /** Quanto do formulário está preenchido. Dá noção de progresso; não valida nada. */
  protected readonly filledPercent = computed(() => {
    this.formTouch();
    const fields = this.steps()
      .flatMap((step) => OccurrenceForm.STEP_FIELDS[step.key] ?? [])
      .filter((name) => !OccurrenceForm.AUTO_FILLED.includes(name));
    const controls = fields.map((name) => this.form.get(name)).filter((c) => c !== null);
    if (!controls.length) return 0;
    const filled = controls.filter((control) => hasValue(control!.value)).length;
    return Math.round((filled / controls.length) * 100);
  });

  /** Bate a cada mudança do formulário, para os `computed` acima saberem que há novidade. */
  private readonly formTouch = signal(0);

  /**
   * Manda recalcular as marcas dos passos e a percentagem.
   *
   * <p>Precisa de existir porque nem tudo o que interessa passa por `valueChanges`: o
   * carregamento entra com `emitEvent: false`, e marcar campos como tocados ou recusados não
   * muda valor nenhum.
   */
  /**
   * As ações vivem fora do formulário: quando mudam, as marcas dos passos ficam velhas — e o
   * estado também pode ter mudado. Sair a última ação de uma ocorrência Em seguimento devolve-a a
   * Em tratamento (revisão de 2026-10-19), e o formulário ficava a mandar o estado antigo.
   */
  protected refreshStepMarksFromChild(): void {
    // Acrescentar uma ação (ou tirá-la) mexe no que o servidor recusou há pouco: o aviso antigo
    // ("acrescente pelo menos uma ação") ficava no rodapé a contradizer o que está à vista.
    this.serverError.set(null);
    this.serverFieldErrors.set([]);
    this.refreshStepMarks();
    const id = this.id();
    if (id === null) return;
    this.service.get(id).subscribe((saved) => {
      if (saved.state === this.originalState()) return;
      this.originalState.set(saved.state);
      this.state.set(saved.state);
      this.form.controls.state.setValue(saved.state, { emitEvent: false });
      this.syncStateValidators();
      this.refreshStepMarks();
    });
  }

  private refreshStepMarks(): void {
    this.formTouch.update((count) => count + 1);
    // Corrigido o último campo, o aviso do rodapé sai sozinho. Antes só saía à gravação
    // seguinte, e ficava a dizer que faltavam campos com tudo já preenchido.
    if (this.hasBlockingErrors() && !Object.values(this.stepStates()).includes('erro')) {
      this.hasBlockingErrors.set(false);
    }
  }

  protected goToStep(key: string): void {
    this.step.set(key);
  }

  protected previousStep(): void {
    const keys = this.steps().map((step) => step.key);
    const index = keys.indexOf(this.step());
    if (index > 0) this.step.set(keys[index - 1]);
  }

  protected nextStep(): void {
    const keys = this.steps().map((step) => step.key);
    const index = keys.indexOf(this.step());
    if (index >= 0 && index < keys.length - 1) this.step.set(keys[index + 1]);
  }

  protected readonly isFirstStep = computed(() => this.steps()[0]?.key === this.step());
  protected readonly isLastStep = computed(() => {
    const keys = this.steps().map((step) => step.key);
    const index = keys.indexOf(this.step());
    return index === -1 || index === keys.length - 1;
  });
  protected readonly serverError = signal<string | null>(null);
  /** O que o servidor recusou, campo a campo. Vazio quando a recusa não nomeia campos. */
  protected readonly serverFieldErrors = signal<string[]>([]);
  protected readonly isEdit = computed(() => this.id() !== null);

  /**
   * O separador aberto vive no endereço: o refresh e o Voltar do browser regressam ao mesmo
   * separador, em vez de caírem no que o link trazia quando o painel abriu.
   */
  private readonly keepStepInUrl = effect(() => {
    const step = this.step();
    if (!this.isEdit()) return;
    untracked(() => {
      if (this.route.snapshot.queryParamMap.get('step') === step) return;
      this.router.navigate([], {
        relativeTo: this.route, queryParams: { step }, queryParamsHandling: 'merge', replaceUrl: true,
      });
    });
  });

  /** Âmbito escolhido à espera de confirmação (a mudança limpa a classificação dependente). */
  protected readonly pendingScope = signal<{ value: OccurrenceScope | null } | null>(null);

  // Derivadas: ligação (permanente) à ocorrência-mãe
  protected readonly parentId = signal<number | null>(null);
  /** Caso crítico do GBT Admin de onde se veio (?casoCritico=): liga a ocorrência ao caso. */
  protected readonly criticalCaseId = signal<number | null>(null);
  protected readonly criticalCaseTarget = signal<string | null>(null);
  protected readonly criticalCaseError = signal<string | null>(null);
  /** Entrada de seguimento de onde esta ocorrência nasceu (chega por query param). */
  protected readonly followUpId = signal<number | null>(null);
  protected readonly parentCode = signal<string | null>(null);
  protected readonly parentCandidates = signal<{ id: number; label: string }[]>([]);

  // Espelhos da cascata (a fonte é o form; sinais permitem computed p/ as opções).
  private readonly scope = signal<OccurrenceScope | null>(null);
  /** O mesmo âmbito, legível pelos `computed` protegidos (o sinal acima é privado). */
  protected readonly scopeValue = this.scope.asReadonly();
  private readonly subScope = signal<OccurrenceSubScope | null>(null);
  private readonly systems = signal<IntegratedSystem[]>([]);
  private readonly type = signal<OccurrenceType | null>(null);
  protected readonly origin = signal<OccurrenceOrigin | null>(null);
  private readonly originDetail = signal<OccurrenceOriginDetail | null>(null);
  private readonly participant = signal<ParticipantIdentification | null>(null);
  private readonly nature = signal<OccurrenceNature | null>(null);
  private readonly processArea = signal<ProcessArea | null>(null);
  private readonly complaintReason = signal<ComplaintReason | null>(null);
  private readonly participantRelation = signal<OrganizationRelation | null>(null);
  private readonly dataSubjectRelation = signal<OrganizationRelation | null>(null);
  private readonly effectiveness = signal<EffectivenessEvaluation | null>(null);
  protected readonly incidentNatures = signal<IncidentNature[]>([]);
  protected readonly reportCategories = signal<ReportCategory[]>([]);
  /** Responsáveis possíveis: a equipa da empresa (o documento pede uma lista, não texto). */
  protected readonly teamMembers = signal<User[]>([]);
  /** Situação escolhida no formulário — o fecho abre os campos do desfecho. */
  protected readonly situation = signal<OccurrenceSituation>('ABERTO');
  protected readonly state = signal<OccurrenceState>('REGISTADO');
  /** O estado com que a ocorrência foi carregada — é dele que se mede a transição pedida. */
  private readonly originalState = signal<OccurrenceState>('REGISTADO');
  /**
   * A situação gravada. O botão da etapa olha para esta e não para a do formulário: concluir uma
   * análise recusada escreve FECHADO no formulário, e se a gravação fosse recusada a seguir o
   * botão desaparecia a meio do caminho.
   */
  protected readonly originalSituation = signal<OccurrenceSituation>('ABERTO');

  /**
   * O botão que põe a ocorrência no estado desta secção, enquanto ela ainda lá não chegou.
   *
   * <p>É a porta da secção, e abre para dentro: entrar num estado não exige o que a secção dele
   * pede — exige o que ficou para trás —, por isso o botão abre o trabalho em vez de o cobrar.
   * Quem já passou por aqui não o vê: fica com o "Guardar" de sempre.
   *
   * <p>Olha o estado gravado e não o que está escolhido no seletor da Identificação: quem mexeu
   * no seletor está a mudar o estado à mão e não precisa do atalho.
   */
  protected readonly startAction = computed<{ label: string; state: OccurrenceState } | null>(() => {
    if (!this.isEdit() || this.originalSituation() === 'FECHADO') return null;
    const target = OccurrenceForm.STEP_STATE[this.step()];
    if (!target) return null;
    // Só o estado logo a seguir: mais à frente o botão existiria para ser recusado.
    if (STATE_ORDER.indexOf(target) !== STATE_ORDER.indexOf(this.originalState()) + 1) return null;
    if (OccurrenceForm.NO_START_BUTTON.includes(target)) return null;
    return { label: START_LABELS[target]!, state: target };
  });

  /** Põe a ocorrência no estado da secção, gravando pelo caminho o que já lá estiver escrito. */
  protected startStage(): void {
    const action = this.startAction();
    if (!action) return;
    this.advanceState(action.state);
    this.submit();
  }

  /**
   * O estado em que o formulário estava antes de um botão de etapa o mudar, enquanto a gravação
   * dessa etapa não volta. Null quando nenhuma etapa está a meio.
   */
  private stateBeforeStage: OccurrenceState | null = null;

  /** Um botão de etapa muda o estado — e lembra-se do anterior, para o devolver se falhar. */
  private advanceState(target: OccurrenceState): void {
    this.stateBeforeStage ??= this.form.controls.state.value;
    this.form.controls.state.setValue(target);
  }

  /**
   * A etapa não passou: o estado volta ao que estava.
   *
   * <p>Ficava o novo. Quem carregava em "Concluir tratamento" sem ação nenhuma recebia a recusa,
   * acrescentava a ação, carregava em "Guardar" — e a ocorrência passava a Em seguimento sem ter
   * concluído coisa nenhuma (revisão de 2026-10-15).
   */
  private undoStageState(): void {
    // A triagem também volta (QA de 2026-10-21): o "Aceite" escreve o resultado e a data antes de
    // gravar, e com a gravação recusada ficavam lá — o "Guardar" seguinte gravava a ocorrência
    // Em análise com a triagem aceite.
    if (this.triageBeforeStage !== null) {
      this.form.controls.triageResult.setValue(this.triageBeforeStage.result);
      this.form.controls.triageDecidedAt.setValue(this.triageBeforeStage.decidedAt);
      this.triageBeforeStage = null;
    }
    if (this.stateBeforeStage === null) return;
    this.form.controls.state.setValue(this.stateBeforeStage);
    this.stateBeforeStage = null;
  }

  /** A triagem antes do "Aceite", enquanto a gravação dele não volta. */
  private triageBeforeStage: { result: TriageResult | null; decidedAt: string } | null = null;

  /**
   * Secção a abrir assim que a gravacão volte, em vez de sair para a ficha.
   *
   * <p>Quem conclui uma etapa fica onde o trabalho continua: "Aceite -> passa para o estado Em
   * tratamento e abre os campos da secção Tratamento para preenchimento" (documento de
   * 2026-10-08). Sair para a ficha obrigava a procurar o "Editar" da secção seguinte.
   */
  private stepAfterSave: string | null = null;

  /** Seguimento: regista-se a partir de Em tratamento, e conta o estado gravado. */
  protected readonly canRegisterFollowUp = computed(
    () => STATE_ORDER.indexOf(this.originalState()) >= STATE_ORDER.indexOf('EM_TRATAMENTO'));

  /** O separador já tem alguma coisa escrita por alguém? Os prazos automáticos não contam. */
  private hasContent(key: string): boolean {
    return (OccurrenceForm.STEP_FIELDS[key] ?? [])
      .filter((name) => !OccurrenceForm.AUTO_FILLED.includes(name))
      .some((name) => hasValue(this.form.get(name)?.value));
  }

  /**
   * Concluir o tratamento: passa a Em seguimento. Só na secção do Tratamento de uma ocorrência
   * que está a ser tratada — o mesmo desenho da análise (documento de 2026-10-08).
   */
  protected readonly canConcludeTreatment = computed(() => this.isEdit()
    && this.originalSituation() !== 'FECHADO'
    && this.step() === 'tratamento'
    && this.originalState() === 'EM_TRATAMENTO');

  protected concludeTreatment(): void {
    this.advanceState('EM_MONITORIZACAO');
    this.stepAfterSave = 'monitorizacao';
    this.submit();
  }

  /** Concluir a análise só se oferece na Análise de uma ocorrência que está a ser analisada. */
  protected readonly canConcludeAnalysis = computed(() => this.isEdit()
    && this.originalSituation() !== 'FECHADO'
    && this.step() === 'analise'
    && this.originalState() === 'EM_ANALISE');

  /** A escolha da triagem, aberta pelo "Concluir análise". */
  protected readonly triageChoice = signal(false);

  protected openTriageChoice(): void {
    this.triageChoice.set(true);
  }

  protected cancelTriageChoice(): void {
    this.triageChoice.set(false);
  }

  /**
   * A triagem decide o destino da ocorrência: aceite leva-a ao tratamento, não aceite fecha-a
   * com o tipo de fecho "fechado após análise" (documento de 2026-10-06).
   *
   * <p>A recusa não grava já. Fechar exige dizer o resultado e o desfecho, e esses campos
   * aparecem aqui mesmo, na Análise, onde a decisão se toma — gravar sem eles era mandar a
   * pessoa à procura deles noutra secção, para receber uma recusa pelo caminho.
   */
  protected concludeAnalysis(accepted: boolean): void {
    this.triageChoice.set(false);
    if (accepted) {
      this.triageBeforeStage ??= {
        result: this.form.controls.triageResult.value,
        decidedAt: this.form.controls.triageDecidedAt.value,
      };
    }
    this.form.controls.triageResult.setValue(accepted ? 'ACEITE' : 'NAO_ACEITE');
    if (!this.form.controls.triageDecidedAt.value) {
      this.form.controls.triageDecidedAt.setValue(this.today);
    }
    if (accepted) {
      this.advanceState('EM_TRATAMENTO');
      this.stepAfterSave = 'tratamento';
      this.submit();
      return;
    }
    this.form.controls.situation.setValue('FECHADO');
    this.form.controls.closingType.setValue('FECHADO_APOS_ANALISE');
    this.refreshStepMarks();
  }

  /** O rodapé tem um botão de etapa — o "Guardar" deixa de ser o destaque. */
  protected readonly stageButton = computed(() => this.startAction() !== null
    || this.canConcludeAnalysis() || this.canConcludeTreatment() || this.stageCloses());

  /**
   * Desfaz a triagem recusada, antes de gravar.
   *
   * <p>Escolhido o "não aceite", o botão passava a ser só "Fechar ocorrência" e não havia por
   * onde mudar de ideias: a escolha da triagem já não voltava a aparecer.
   */
  protected undoTriageChoice(): void {
    this.form.controls.triageResult.setValue(null);
    this.form.controls.situation.setValue('ABERTO');
    this.form.controls.closingType.setValue(null);
    this.form.controls.closingResult.setValue('');
    this.form.controls.outcome.setValue(null);
    this.refreshStepMarks();
  }

  /** Com a análise recusada, os campos do fecho passam a aparecer dentro da Análise. */
  protected readonly stageCloses = computed(() => {
    this.formTouch();
    return this.step() === 'analise'
      && this.form.controls.closingType.value === 'FECHADO_APOS_ANALISE'
      && this.form.controls.situation.value === 'FECHADO';
  });

  /**
   * Os estados que se podem escolher. Depois de a ocorrência sair de Registado não volta lá:
   * "após o concluir tratamento já aparecem listados todos os estados, exceto o registado".
   */
  protected readonly stateChoices = computed(() => this.originalState() === 'REGISTADO'
    ? STATE_OPTIONS
    : STATE_OPTIONS.filter((option) => option.value !== 'REGISTADO'));

  // --- Classificação (Etapa 1)
  protected readonly showScopeOther = computed(() => this.scope() === 'OUTRO');
  /**
   * O subâmbito só se pede a âmbitos que tenham subâmbitos.
   *
   * <p>Antes bastava não ser "Outro", e a Segurança da Informação — que o documento deixa "a
   * definir posteriormente" — mostrava uma lista vazia e ainda assim exigia uma escolha: a
   * ocorrência não se conseguia gravar, e o separador ficava a vermelho sem nenhum campo
   * vermelho lá dentro. O servidor sempre disse o contrário ("este âmbito ainda não tem
   * subâmbitos"); é ele que manda.
   *
   * <p>Ler do catálogo em vez de nomear âmbitos resolve-o para sempre: no dia em que os
   * subâmbitos existirem, o campo aparece sozinho.
   */
  protected readonly showSubScope = computed(() => this.subScopeOptions().length > 0);
  protected readonly showIntegratedSystems = computed(() => this.subScope() === 'SISTEMA_INTEGRADO');
  protected readonly showSubScopeOther = computed(
    () => this.subScope() === 'OUTRO_SISTEMA'
      || (this.subScope() === 'SISTEMA_INTEGRADO' && this.systems().includes('OUTRO_SISTEMA')),
  );
  protected readonly showTypeOther = computed(() => this.type() === 'OUTRO');

  protected readonly subScopeOptions = computed(() => {
    const scope = this.scope();
    if (!scope) return [];
    return SCOPE_SUB_SCOPES[scope].map((value) => ({ value, label: SUB_SCOPE_LABELS[value] }));
  });

  protected readonly typeOptions = computed(() =>
    typesFor(this.scope(), this.subScope()).map((value) => ({ value, label: TYPE_LABELS[value] })));

  // --- Natureza: só na Participação/Comunicação, e é ela que manda no resto
  protected readonly showNature = computed(() => this.type() === 'PARTICIPACAO_COMUNICACAO');

  protected readonly natureOptions = computed(() =>
    naturesFor(this.scope(), this.subScope(), this.systems())
      .map((value) => ({ value, label: NATURE_LABELS[value] })));

  protected readonly isComplaint = computed(() => this.nature() === 'RECLAMACAO');
  protected readonly isReport = computed(() => this.nature() === 'DENUNCIA');

  protected readonly showComplaintOther = computed(() => this.complaintReason() === 'OUTRO');

  /** "Outra irregularidade" sem dizer qual não diz nada. */
  protected readonly showReportCategoryOther = computed(
    () => this.reportCategories().includes('OUTRA_IRREGULARIDADE'));
  /**
   * A denúncia de proteção de dados não admite anonimato (folha dos campos do canal): o caso tem
   * de se poder relacionar com o titular dos dados.
   */
  protected readonly reportRequiresIdentification = computed(
    () => this.isReport() && this.scope() === 'RGPD');

  /** Processo/área acompanha os sistemas de gestão; nas reclamações é obrigatório. */
  protected readonly showProcessArea = computed(() => this.scope() === 'SISTEMAS_GESTAO');
  protected readonly showProcessAreaOther = computed(() => this.processArea() === 'OUTRO');

  /** A origem com que a ocorrência foi carregada — "Site" só fica na lista de quem veio de lá. */
  private readonly loadedOrigin = signal<OccurrenceOrigin | null>(null);

  // --- Origem: uma lista só, do âmbito. Escolhe-se antes do tipo desde 2026-10-05.
  /**
   * "Site" não se escolhe à mão: o que chega pelo site regista-se sozinho (revisão de
   * 2026-10-15). Fica na lista só para a ocorrência que já veio de lá, para a edição a mostrar.
   */
  protected readonly originOptions = computed(() =>
    originsFor(this.scope(), this.subScope())
      .filter((value) => value !== 'SITE' || this.loadedOrigin() === 'SITE')
      .map((value) => ({ value, label: ORIGIN_LABELS[value] })));

  /**
   * "Ação de resposta a outra ocorrência" já não se escolhe: a ocorrência que responde a outra tem
   * a origem "Decorrente de outra ocorrência", e é essa que fica fixa enquanto a ligação existir
   * (revisão de 2026-10-19). Fica na lista só de quem já a tem gravada.
   */
  private readonly loadedOriginDetail = signal<OccurrenceOriginDetail | null>(null);

  protected readonly originDetailOptions = computed(() =>
    originDetailsFor(this.origin(), this.scope(), this.subScope(), this.systems(), this.type())
      .filter((value) => value !== 'ACAO_RESPOSTA_OCORRENCIA'
        || this.loadedOriginDetail() === 'ACAO_RESPOSTA_OCORRENCIA')
      .map((value) => ({ value, label: ORIGIN_DETAIL_LABELS[value] })));

  /**
   * A ocorrência derivada não tem detalhe de origem: a origem dela é a outra ocorrência, e o
   * detalhe era uma pergunta a mais numa janela que já vem com a resposta (documento de
   * 2026-10-08).
   */
  protected readonly showOriginDetail = computed(
    () => this.parentId() === null && this.originDetailOptions().length > 0);

  /** Terceiro nível: quem foi avaliado quando a origem é a satisfação. */
  protected readonly showInterestedParty = computed(() => this.originDetail() === 'SATISFACAO');

  protected readonly anonymous = computed(() => this.participant() === 'ANONIMO');

  /** Texto da origem: "Outra" (a própria origem ou o detalhe) ou "quem" (cliente/partes). */
  protected readonly showOriginOther = computed(() => {
    const detail = this.originDetail();
    if (detail && ORIGIN_DETAILS_WITH_OTHER.includes(detail)) return true;
    const origin = this.origin();
    if (origin === 'OUTRA_ORIGEM') return true;
    return origin !== null && ORIGINS_WITH_WHO.includes(origin) && !this.anonymous();
  });

  protected readonly originOtherLabel = computed(() => {
    const origin = this.origin();
    if (origin === 'OUTRA_ORIGEM') return 'Identificar a origem';
    if (origin === 'CLIENTE') return 'Identificar o cliente';
    if (origin === 'OUTRAS_PARTES_INTERESSADAS') return 'Identificar a parte interessada';
    if (this.originDetail() === 'OUTRA_REUNIAO') return 'Identificar a reunião';
    if (this.originDetail() === 'OUTRA_AUDITORIA') return 'Identificar a auditoria';
    if (this.originDetail() === 'OUTRA_AUTORIDADE') return 'Identificar a autoridade/entidade';
    return 'Especificar';
  });

  /**
   * No exercício de direitos também: quem pede é o titular, e é aqui que se identifica. A revisão
   * de 2026-10-15 tirou o bloco "Titular dos dados", e este saía com ele por o pedido não ser uma
   * participação — "um era para manter" (revisão de 2026-10-19).
   */
  protected readonly showParticipant = computed(
    () => this.origin() === 'SITE' || this.showNature() || this.showDataSubject());

  protected readonly showParticipantContacts = computed(
    () => this.showParticipant() && this.participant() === 'IDENTIFICADO');

  protected readonly showWorkerType = computed(() => this.participantRelation() === 'COLABORADOR');

  /**
   * Identificação imposta pelo motivo: produto/serviço e atendimento não admitem anonimato, as
   * sugestões e ideias são sempre identificadas, e a denúncia de proteção de dados também.
   */
  protected readonly identificationLocked = computed(() =>
    (this.isComplaint() && this.complaintReason() !== null
      && REASONS_REQUIRING_IDENTIFICATION.includes(this.complaintReason()!))
    || this.reportCategories().includes('OUTRA_IRREGULARIDADE')
    || this.nature() === 'SUGESTAO_IDEIA'
    || this.reportRequiresIdentification());

  /** RGPD — exercício de direitos: campos do titular; anónimo proibido. */
  protected readonly showDataSubject = computed(() => this.subScope() === 'EXERCICIO_DIREITOS');
  protected readonly showDataSubjectWorkerType = computed(() => this.dataSubjectRelation() === 'COLABORADOR');

  /** A natureza do incidente pertence à conformidade e proteção de dados. */
  /**
   * O campo único "Natureza do incidente ou violação" (documento de 2026-10-08).
   *
   * <p>Eram dois: as "Categorias associadas ao caso" da denúncia e a "Natureza do incidente ou
   * violação" da conformidade de dados — duas listas a pedir a mesma coisa, em dois sítios da
   * mesma seccção. Passou a ser um, com a lista do âmbito.
   */
  protected readonly showIncidentField = computed(
    () => this.isReport() || this.legacyIncidentNatures());

  /**
   * Na conformidade e proteção de dados a lista aparecia logo que se escolhia o subâmbito, mesmo
   * num elogio ou numa sugestão. Desde a revisão de 2026-10-19 é só da denúncia, como no RGPC.
   * Fica à vista nas ocorrências antigas que já a têm preenchida, para a gravação não a apagar.
   */
  private readonly legacyIncidentNatures = signal(false);

  /**
   * Quando a lista é a das naturezas do incidente em vez das categorias do âmbito: na Segurança
   * da Informação, que tem as suas seis, e no registo de violação de dados que não é denúncia,
   * onde este campo já vivia.
   */
  protected readonly showIncidentNatures = computed(() => this.showIncidentField()
    && (this.scope() === 'SEGURANCA_INFORMACAO'
      || (this.legacyIncidentNatures() && !this.isReport())));

  protected readonly showTargetedPersons = computed(() => this.subScope() === 'GESTAO_COMUNICACOES');

  /** O detalhe das partes só interessa quando a denúncia é de conflito de interesses. */
  protected readonly showConflictParties = computed(
    () => this.reportCategories().includes('CONFLITO_INTERESSES'));

  /** "Não foi possível de aferir" tem de dizer porquê. */
  protected readonly showUnableReason = computed(
    () => this.effectiveness() === 'NAO_FOI_POSSIVEL_AFERIR');

  /**
   * "Ter um pisco para Verificação da eficácia aplicável?" (documento de 2026-10-08).
   *
   * <p>Não é campo novo: o pisco é a leitura do resultado da avaliação. Desligado, a avaliação
   * fica <em>Não aplicável</em>, o prazo fica em branco e os restantes campos da secção não se
   * preenchem; ligado, volta ao prazo automático de sempre.
   */
  protected readonly effectivenessApplicable = computed(
    () => this.effectiveness() !== 'NAO_APLICAVEL');

  protected setEffectivenessApplicable(applicable: boolean): void {
    const controls = this.form.controls;
    if (applicable) {
      controls.effectivenessEvaluation.setValue(null);
      const base = controls.actionImplementationDeadline.value;
      if (base && !controls.effectivenessEvaluationDeadline.value) {
        controls.effectivenessEvaluationDeadline.setValue(addMonths(base, 8));
      }
    } else {
      controls.effectivenessEvaluation.setValue('NAO_APLICAVEL');
      controls.effectivenessEvaluationDeadline.setValue('');
      controls.effectivenessEvaluationDate.setValue('');
      controls.effectivenessFindings.setValue('');
      controls.effectivenessUnableReason.setValue('');
    }
    this.form.markAsDirty();
    this.refreshStepMarks();
  }

  /** Origem "Ação de resposta" sem ligação pré-definida: escolher a ocorrência-mãe. */
  /**
   * Escolher a ocorrência-mãe à mão: quando a origem diz que esta decorre de outra e não se
   * chegou aqui a partir de uma linha de seguimento (aí a mãe já vem escolhida).
   */
  protected readonly showParentPicker = computed(() => this.parentId() === null
    && !this.orphan()
    && (this.origin() === 'DECORRENTE_OUTRA_OCORRENCIA'
      || this.originDetail() === 'ACAO_RESPOSTA_OCORRENCIA'));

  /**
   * Derivada cuja mãe foi eliminada: a ligação quebrou-se, e o histórico guarda a que houve
   * (revisão de 2026-10-19). Não se pede outra mãe — a origem continua a ser verdade.
   */
  protected readonly orphan = computed(() => this.isEdit() && this.parentId() === null
    && this.loadedOrigin() === 'DECORRENTE_OUTRA_OCORRENCIA'
    && this.origin() === 'DECORRENTE_OUTRA_OCORRENCIA');

  protected readonly form = this.fb.nonNullable.group({
    title: ['', [Validators.required, Validators.maxLength(OccurrenceForm.TITLE_MAX_LENGTH)]],
    // Opcional desde a revisão de 2026-10-19 (foi obrigatória entre 2026-09-26 e essa data).
    description: [''],
    scope: [null as OccurrenceScope | null, Validators.required],
    scopeOther: [''],
    subScope: [null as OccurrenceSubScope | null],
    subScopeOther: [''],
    type: [null as OccurrenceType | null, Validators.required],
    typeOther: [''],
    nature: [null as OccurrenceNature | null],
    processArea: [null as ProcessArea | null],
    processAreaOther: [''],
    complaintReason: [null as ComplaintReason | null],
    complaintReasonOther: [''],
    reportCategoryOther: [''],
    recurringComplaint: [false],
    origin: [null as OccurrenceOrigin | null, Validators.required],
    originDetail: [null as OccurrenceOriginDetail | null],
    originOther: [''],
    interestedParty: [null as InterestedParty | null],
    parentSelect: [null as number | null],
    participantIdentification: [null as ParticipantIdentification | null],
    participantName: [''],
    participantEmail: [''],
    participantPhone: [''],
    participantConfidential: [false],
    participantRelation: [null as OrganizationRelation | null],
    participantWorkerType: [null as WorkerType | null],
    // Cliente correspondente ao participante: preenchido ao escolher na lista; fica null
    // quando o nome é escrito de novo, e nesse caso o backend cria o cliente ao gravar.
    participantClientId: [null as number | null],
    dataSubjectName: [''],
    dataSubjectContact: [''],
    dataSubjectRelation: [null as OrganizationRelation | null],
    dataSubjectRelationOther: [''],
    dataSubjectWorkerType: [null as WorkerType | null],
    dataSubjectValidated: [false],
    targetedPersons: [''],
    conflictParties: [''],
    occurrenceDate: ['', Validators.required],
    actionImplementationDeadline: [''],
    effectivenessEvaluationDeadline: [''],
    effectivenessEvaluationDate: [''],
    // Blocos de tratamento C–H (visíveis só na edição)
    initialAnalysis: [''],
    causeAnalysis: [''],
    impact: [null as OccurrenceImpact | null],
    requirementReference: [''],
    actionDescription: [''],
    actionResponsibleId: [null as number | null],
    effectivenessEvaluation: [null as EffectivenessEvaluation | null],
    effectivenessFindings: [''],
    effectivenessUnableReason: [''],
    // Triagem, espera de elementos, desfecho e conservação (denúncias e reclamações)
    triageResult: [null as TriageResult | null],
    triageDecidedAt: [''],
    triageDecisionDeadline: [''],
    informDeadline: [''],
    awaitingElements: [false],
    elementsRequestedAt: [''],
    elementsResponseDeadline: [''],
    outcome: [null as OccurrenceOutcome | null],
    legalHold: [false],
    legalHoldNote: [''],
    // Situação e estado — só na edição: o registo nasce sempre Aberto/Registado.
    situation: ['ABERTO' as OccurrenceSituation],
    state: ['REGISTADO' as OccurrenceState],
    closingType: [null as ClosingType | null],
    closingResult: [''],
    closingDate: [''],
    reevaluationDeadline: [''],
  });

  protected readonly closing = computed(() => this.situation() === 'FECHADO');

  /**
   * O resultado do fecho só é obrigatório com o desfecho "Outro" — é aí que explica em que deu o
   * caso; nos restantes o desfecho já o diz (revisão de 2026-10-19).
   */
  protected readonly closingResultRequired = computed(() => {
    this.formTouch();
    return this.form.controls.outcome.value === 'OUTRO';
  });

  /**
   * Denúncias e reclamações são os casos do canal: têm triagem, podem ficar à espera de
   * elementos e fecham com um desfecho da lista. As outras participações não.
   */
  protected readonly isCaseWork = computed(() => this.isReport() || this.isComplaint());
  /**
   * Naturezas com comunicante a quem o documento obriga a responder em 30 dias — as mesmas que
   * o servidor usa para calcular o prazo. A sugestão/ideia entra: tinha prazo calculado e campo
   * nenhum onde o ver.
   */
  protected readonly hasCommunicant = computed(
    () => this.isCaseWork() || this.nature() === 'SUGESTAO_IDEIA');

  /**
   * Ocorrências que se graduam — não conformidades, reclamações e denúncias. São as únicas em
   * que o impacto é exigido, e, dentro dos sistemas de gestão, também o requisito.
   */
  /** Graduáveis: NC, OP, reclamação e denúncia — a OP entrou em 2026-09-26. */
  protected readonly graded = computed(
    () => this.type() === 'NAO_CONFORMIDADE' || this.type() === 'OPORTUNIDADE_MELHORIA'
      || this.isComplaint() || this.isReport());

  /** O requisito deixou de estar preso aos sistemas de gestão: pede-se em qualquer âmbito. */
  protected readonly requirementRequired = this.graded;

  /**
   * Nos casos graves a causa tem de ser apurada — não basta a leitura inicial. A ação corretiva
   * entrou na revisão de 2026-10-19: deixava concluir a análise sem causa nenhuma.
   */
  protected readonly causeRequired = computed(
    () => this.type() === 'NAO_CONFORMIDADE' || this.type() === 'ACAO_CORRETIVA'
      || this.isComplaint() || this.isReport());

  /** Espera de elementos: as datas do pedido só fazem sentido com a espera marcada. */
  protected readonly awaitingElements = signal(false);

  /**
   * Aviso dos dados de saúde. Aparece nas comunicações em que se costuma juntar informação
   * clínica que não é precisa para tratar o caso — assédio, segurança e saúde, condições de
   * trabalho.
   */
  protected readonly showHealthDataNote = computed(() =>
    this.reportCategories().some((category) => HEALTH_DATA_CATEGORIES.includes(category))
    || (this.isComplaint() && this.complaintReason() === HEALTH_DATA_COMPLAINT_REASON));
  /**
   * A secção é exigida pelo estado escolhido? As secções acumulam ao longo do ciclo — a mesma
   * regra que o backend aplica antes de deixar a ocorrência mudar de estado.
   */
  /** Encostado ao canto: o painel continua inteiro, só muda de geometria. */
  protected readonly minimized = signal(false);

  /**
   * A animação de abertura toca uma vez, quando o painel abre. A partir do primeiro encostar
   * fica desligada para sempre: o voo entre o canto e o meio é do FLIP, e deixá-la ligada fazia
   * o painel aterrar e logo a seguir desaparecer e reaparecer do princípio.
   */
  protected readonly entryDone = signal(false);

  ngAfterViewInit(): void {
    // showModal() e não o atributo `open`: é o que põe o painel na camada de topo e prende lá
    // o foco. Com o atributo, ficaria a ser um elemento normal da página.
    this.dialog()?.nativeElement.showModal();
  }

  ngOnDestroy(): void {
    document.documentElement.classList.remove('has-docked-form');
  }

  /**
   * Encosta o painel ao canto inferior direito, ou traz--o de volta ao meio.
   *
   * <p>O painel não é destruído: o que está escrito continua lá, e é isso que dá sentido a
   * minimizar em vez de fechar. Encostado deixa de ser modal, para a lista por baixo voltar a
   * responder ao rato — um `<dialog>` aberto com `showModal()` torna inerte todo o resto.
   */
  protected toggleMinimize(): void {
    const dialog = this.dialog()?.nativeElement;
    const surface = this.surface()?.nativeElement;
    if (!dialog || !surface) return;
    const docking = !this.minimized();

    this.entryDone.set(true);
    this.animateGeometryChange(surface, () => {
      this.minimized.set(docking);
      document.documentElement.classList.toggle('has-docked-form', docking);
      dialog.close();
      if (docking) {
        dialog.show();
      } else {
        dialog.showModal();
      }
    });
  }

  /**
   * Anima a mudança de geometria com FLIP: mede antes, aplica, mede depois, e faz o painel
   * partir visualmente do sítio antigo até ao novo.
   *
   * <p>Sem isto, encostar era um salto: a posição vem do alinhamento do contentor, que muda de
   * uma vez e não se anima. Com FLIP anima-se sempre a mesma coisa — uma transformação —, que é
   * o que dá a leitura de janela a encolher e a crescer.
   */
  private animateGeometryChange(element: HTMLElement, apply: () => void): void {
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    if (reduceMotion) {
      apply();
      return;
    }

    const first = element.getBoundingClientRect();
    apply();
    // Entre aplicar e medir não há pintura — o browser só pinta no fim desta tarefa —, por isso
    // forçar a deteção aqui dá o tamanho final sem qualquer salto visível.
    this.appRef.tick();
    // A altura do corpo é escrita por um observador assíncrono; ao voltar do canto ele ainda não
    // reagiu, e mediríamos a altura zero do estado encostado.
    this.autoHeight()?.syncNow();
    const last = element.getBoundingClientRect();
    if (!first.width || !last.width) return;

    element.style.transition = 'none';
    element.style.transformOrigin = 'top left';
    element.style.transform = `translate(${first.left - last.left}px, ${first.top - last.top}px)`
      + ` scale(${first.width / last.width}, ${first.height / last.height})`;
    void element.getBoundingClientRect(); // sem esta leitura os dois estados juntam-se num só

    element.style.transition = `transform ${DOCK_ANIMATION_MS}ms ${DOCK_EASING}`;
    element.style.transform = 'translate(0, 0) scale(1, 1)';

    setTimeout(() => {
      element.style.removeProperty('transition');
      element.style.removeProperty('transform');
      element.style.removeProperty('transform-origin');
    }, DOCK_ANIMATION_MS);
  }

  /**
   * Fecha o painel, voltando ao que está por baixo — a lista, ao registar ou ao editar a partir
   * dela; a ocorrência, ao editar a partir da ficha. Não grava ao sair: quem sai sem gravar perde o que escreveu.
   *
   * <p>A ocorrência derivada é a exceção: abre-se a partir de uma linha de seguimento de outra,
   * e desistir dela atirava a pessoa para a lista, longe do sítio onde estava a trabalhar.
   * Volta-se à ocorrência de onde ela nasceu.
   */
  protected close(): void {
    // Aberto pelo lápis da lista: o Cancelar volta à lista, não à ficha que nunca se viu
    // (revisão de 2026-10-19).
    if (this.route.snapshot.queryParamMap.get('origem') === 'lista') {
      this.router.navigate(['/', this.auth.currentTenant(), 'ocorrencias']);
      return;
    }
    const mae = this.parentId();
    if (!this.isEdit() && mae !== null) {
      this.router.navigate(['/', this.auth.currentTenant(), 'ocorrencias', mae]);
      return;
    }
    this.router.navigate(['.'], { relativeTo: this.route.parent });
  }

  /**
   * Avisa quem gravou a secção do estado em que a ocorrência está sem lá ter escrito nada.
   *
   * <p>Não impede: há quem ponha a ocorrência em análise e só depois se sente a analisar. Mas
   * fica dito, para ninguém pensar que a secção ficou preenchida.
   */
  private warnEmptyStage(saved: Occurrence): void {
    const daSeccao = OccurrenceForm.STEP_STATE[this.step()];
    if (!daSeccao || daSeccao !== saved.state || this.hasContent(this.step())) return;
    const nome = this.steps().find((step) => step.key === this.step())?.label ?? 'secção';
    this.toasts.error(
      `${saved.code} ficou ${STATE_LABELS[saved.state]} com a ${nome} por preencher.`);
  }

  /** Acabou de gravar e está em Registado: id da ocorrência à espera da resposta sobre a análise. */
  protected readonly askStartAnalysis = signal<number | null>(null);

  /** Sim: põe a ocorrência em Em análise e abre a secção para preencher. */
  protected confirmStartAnalysis(): void {
    const id = this.askStartAnalysis();
    this.askStartAnalysis.set(null);
    if (id === null) return;
    this.loading.set(true);
    this.service.changeState(id, {
      state: 'EM_ANALISE', situation: 'ABERTO', closingType: null, closingResult: null,
      outcome: null, closingDate: null, reevaluationDeadline: null,
      effectivenessEvaluationDate: null,
    }).subscribe({
      next: () => {
        this.loading.set(false);
        this.router.navigate(['/', this.auth.currentTenant(), 'ocorrencias', id, 'editar'],
          { queryParams: { step: 'analise' } });
      },
      error: () => {
        this.loading.set(false);
        this.toasts.error('Não foi possível pôr a ocorrência em análise.');
        this.router.navigate(['/', this.auth.currentTenant(), 'ocorrencias', id]);
      },
    });
  }

  /** Agora não: fica em Registado e volta-se à ocorrência. */
  protected declineStartAnalysis(): void {
    const id = this.askStartAnalysis();
    this.askStartAnalysis.set(null);
    if (id !== null) {
      this.router.navigate(['/', this.auth.currentTenant(), 'ocorrencias', id]);
    }
  }

  /** Saída pedida com o formulário mexido — à espera de resposta. */
  protected readonly pendingClose = signal(false);

  /**
   * Sair deita fora o que não foi gravado, por isso com o formulário mexido pergunta-se antes.
   *
   * <p>O Esc e o clique no fundo são fáceis de dar sem querer — e numa secção já escrita levavam
   * tudo sem aviso nenhum.
   */
  protected requestClose(): void {
    if (this.form.dirty) {
      this.pendingClose.set(true);
      return;
    }
    this.close();
  }

  /** À espera da resposta de quem carregou em Voltar do browser com o formulário mexido. */
  private leaveAnswer?: (leave: boolean) => void;

  /**
   * Guarda de saída da rota. Só o Voltar/Avançar do browser passa por aqui: o resto (X, Esc, fundo,
   * Cancelar) já pergunta por si, e o que se faz depois de gravar navega de propósito. Sem isto, o
   * Voltar fechava o painel e levava tudo o que estava escrito sem um aviso.
   */
  canLeave(): boolean | Promise<boolean> {
    if (!this.form.dirty || this.router.getCurrentNavigation()?.trigger !== 'popstate') return true;
    this.pendingClose.set(true);
    return new Promise<boolean>((resolve) => (this.leaveAnswer = resolve));
  }

  protected confirmClose(): void {
    this.pendingClose.set(false);
    if (this.leaveAnswer) {
      this.leaveAnswer(true);
      this.leaveAnswer = undefined;
      return;
    }
    this.close();
  }

  protected cancelClose(): void {
    this.pendingClose.set(false);
    this.leaveAnswer?.(false);
    this.leaveAnswer = undefined;
  }

  /** Esc: o browser fecharia o `<dialog>` e deixava a rota como estava, com a página em branco. */
  protected onDialogCancel(event: Event): void {
    event.preventDefault();
    // Com a escolha da triagem aberta, o Esc desfaz só a escolha.
    if (this.triageChoice()) {
      this.cancelTriageChoice();
      return;
    }
    this.requestClose();
  }

  /**
   * Clique no fundo escurecido. O fundo de um `<dialog>` faz parte da caixa do próprio elemento,
   * por isso o que o distingue é o alvo do clique ser o `<dialog>` e não algo lá dentro.
   */
  protected onDialogClick(event: MouseEvent): void {
    if (event.target === this.dialog()?.nativeElement) {
      this.requestClose();
    }
  }

  protected requiredFrom(state: OccurrenceState): boolean {
    return this.isEdit() && STATE_ORDER.indexOf(this.state()) >= STATE_ORDER.indexOf(state);
  }

  /**
   * As duas regras de transição do documento de 2026-09-26, ou null quando não há problema.
   *
   * <p>O servidor recusa-as de qualquer maneira; dizê-las aqui poupa uma ida e volta e explica
   * o que fazer em vez de devolver um erro no fim do formulário.
   */
  private transitionProblem(): string | null {
    if (!this.isEdit()) return null;
    const original = this.originalState();
    if (original === 'REGISTADO' && this.state() === 'EM_TRATAMENTO') {
      return 'De Registado não se passa direto a Em tratamento: passe primeiro por Em análise.';
    }
    // Fechar é o fim do ciclo. A saída é para os casos que não chegam a ser tratados.
    const outcome = this.form.controls.outcome.value;
    const earlyClosing = outcome === 'NAO_ACEITE_NESTE_CANAL' || outcome === 'ENCERRADO_FALTA_ELEMENTOS'
      // O que a triagem recusa fecha-se na análise (documento de 2026-10-03).
      || this.form.controls.closingType.value === 'FECHADO_APOS_ANALISE';
    // Sem verificação da eficácia, o ciclo acaba em Em seguimento (revisão de 2026-10-12).
    const endOfCycle = this.state() === 'AVALIACAO_REAVALIACAO'
      || (this.state() === 'EM_MONITORIZACAO' && !this.effectivenessApplicable());
    if (this.closing() && !endOfCycle && !earlyClosing) {
      return this.effectivenessApplicable()
        ? 'Só se fecha a partir de Em avaliação — ou com um desfecho de '
          + '"não aceite neste canal" ou "encerrado por falta de elementos".'
        : 'Só se fecha a partir de Em seguimento ou de Em avaliação — ou com um desfecho de '
          + '"não aceite neste canal" ou "encerrado por falta de elementos".';
    }
    return null;
  }

  ngOnInit(): void {
    // O `id` vive na rota-mãe (o detalhe da ocorrência): o painel é filho dela, para o que está
    // por baixo continuar montado enquanto se edita.
    const idParam = this.route.parent?.snapshot.paramMap.get('id') ?? null;
    if (idParam) {
      this.id.set(Number(idParam));
      this.service.get(Number(idParam)).subscribe((occurrence) => this.patch(occurrence));
    }
    // Cada secção da página da ocorrência tem o seu "Editar", e traz o separador no ?step=:
    // sem isto, clicar em Editar na Análise abria o formulário na Caracterização.
    const stepParam = this.route.snapshot.queryParamMap.get('step');
    if (stepParam && stepParam in OccurrenceForm.STEP_FIELDS) this.step.set(stepParam);
    // Fluxo "abrir nova ocorrência para responder": chega com ?parentId= e liga a mãe.
    const followUpParam = this.route.snapshot.queryParamMap.get('followUpId');
    if (followUpParam) this.followUpId.set(Number(followUpParam));
    // "Replicar": uma ocorrência nova com a classificação e a descrição de outra, sem ligação.
    const replicaParam = this.route.snapshot.queryParamMap.get('replicarDe');
    if (!idParam && replicaParam) {
      this.service.get(Number(replicaParam)).subscribe((source) => this.patchReplica(source));
    }
    const parentParam = this.route.snapshot.queryParamMap.get('parentId');
    if (!idParam && parentParam) {
      const parentId = Number(parentParam);
      this.parentId.set(parentId);
      this.service.get(parentId).subscribe((parent) => {
        this.parentCode.set(parent.code);
        this.inheritFromParent(parent);
        // Origem da derivada (documento de 2026-10-08), fixa enquanto a ligação existir (revisão
        // de 2026-10-19). Era "Avaliação/monitorização" com detalhe "Ação de resposta".
        this.form.controls.origin.setValue('DECORRENTE_OUTRA_OCORRENCIA');
        this.lockDerivedOrigin();
      });
    }
    // Fluxo "Abrir ocorrência" a partir de um caso crítico: carrega o caso e pré-preenche.
    const caseParam = Number(this.route.snapshot.queryParamMap.get('casoCritico'));
    if (!idParam && Number.isInteger(caseParam) && caseParam > 0) {
      this.criticalCaseId.set(caseParam);
      this.criticalCases.get(caseParam).subscribe({
        next: (detail) => this.prefillFromCriticalCase(detail),
        error: (err: HttpErrorResponse) => this.criticalCaseError.set(
          (err.error as { message?: string } | null)?.message
          ?? (err.status === 404 ? 'O caso crítico não foi encontrado.'
            : 'Não foi possível carregar o caso crítico.')),
      });
    }
    this.users.team().subscribe({
      next: (list) => this.teamMembers.set(list.filter((user) => user.status === 'ACTIVE')),
      error: () => this.teamMembers.set([]),
    });
    this.watchScope();
    this.watchSubScope();
    this.watchType();
    this.watchOrigin();
    this.watchParticipant();
    this.watchNature();
    this.watchStateAndSituation();
    this.form.controls.occurrenceDate.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      if (value) this.form.controls.actionImplementationDeadline.setValue(addMonths(value, 6));
    });
    this.form.controls.actionImplementationDeadline.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        // Dispensada a verificação da eficácia, o prazo fica em branco e não volta sozinho.
        if (value && this.effectivenessApplicable()) {
          this.form.controls.effectivenessEvaluationDeadline.setValue(addMonths(value, 8), { emitEvent: false });
        }
      });
  }

  /**
   * Mudar o âmbito limpa o subâmbito, o tipo e os dependentes — após confirmação no diálogo da
   * aplicação. Como a confirmação é assíncrona, a seleção é revertida já e só se aplica no "sim".
   */
  private watchScope(): void {
    this.form.controls.scope.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((scope) => {
      if (scope === this.scope()) return;
      const hasDependents = this.form.controls.subScope.value !== null || this.form.controls.type.value !== null;
      if (hasDependents) {
        this.form.controls.scope.setValue(this.scope(), { emitEvent: false });
        this.pendingScope.set({ value: scope });
        return;
      }
      this.applyScope(scope);
    });
  }

  protected confirmScopeChange(): void {
    const pending = this.pendingScope();
    this.pendingScope.set(null);
    if (!pending) return;
    this.form.controls.scope.setValue(pending.value, { emitEvent: false });
    this.applyScope(pending.value);
  }

  protected cancelScopeChange(): void {
    this.pendingScope.set(null);
  }

  private applyScope(scope: OccurrenceScope | null): void {
    this.clearSubScope();
    this.clearType();
    this.scope.set(scope);
    this.form.controls.scopeOther.setValidators(scope === 'OUTRO' ? Validators.required : null);
    this.form.controls.scopeOther.reset('', { emitEvent: false });
    this.revalidateOrigin();
    this.revalidateOriginDetail();
    this.syncStateValidators();
  }

  /** Mudar o subâmbito limpa o tipo apenas se ficou incompatível. */
  private watchSubScope(): void {
    this.form.controls.subScope.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((subScope) => {
      if (subScope === this.subScope()) return;
      this.subScope.set(subScope);
      this.systems.set([]);
      this.form.controls.subScopeOther.reset('', { emitEvent: false });
      const type = this.form.controls.type.value;
      if (type && !typesFor(this.scope(), subScope).includes(type)) this.clearType();
      this.revalidateOrigin();
      this.revalidateOriginDetail();
      this.revalidateComplaint();
    });
  }

  /**
   * Trocar o subâmbito pode tirar as reclamações do mapa — e aí diz-se, em vez de deixar lá
   * uma natureza que aquele subâmbito não conhece (documento de 2026-10-08).
   */
  private revalidateComplaint(): void {
    const control = this.form.controls.nature;
    const allowed = complaintsAllowed(this.scope(), this.subScope(), this.systems());
    if (control.value === 'RECLAMACAO' && !allowed) {
      control.setErrors({ ...(control.errors ?? {}), subScope: true });
      control.markAsTouched();
      this.refreshStepMarks();
      return;
    }
    if (control.hasError('subScope')) {
      const { subScope: _removido, ...resto } = control.errors ?? {};
      control.setErrors(Object.keys(resto).length ? resto : null);
      this.refreshStepMarks();
    }
  }

  /**
   * Mudar o estado deixou de exigir sair da edição: os campos do fecho e o prazo de
   * reavaliação aparecem conforme a escolha, como no card "Mudar estado" do detalhe.
   */
  private watchStateAndSituation(): void {
    // Barra de progresso e marcas dos passos leem o formulário, que não é um sinal.
    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.refreshStepMarks());

    this.form.controls.situation.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((situation) => {
        this.situation.set(situation);
        if (situation === 'FECHADO' && !this.form.controls.closingDate.value) {
          this.form.controls.closingDate.setValue(isoToday(), { emitEvent: false });
        }
        // Com a verificação da eficácia aplicável, o fecho proposto é "Sujeito a verificação":
        // ela faz-se uns seis meses depois (revisão de 2026-10-12). Só propõe — troca-se.
        if (situation === 'FECHADO' && !this.form.controls.closingType.value
            && this.effectivenessApplicable()) {
          this.form.controls.closingType.setValue('SUJEITO_VERIFICACAO', { emitEvent: false });
        }
        this.syncStateValidators();
      });
    this.form.controls.state.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((state) => {
      this.state.set(state);
      this.syncStateValidators();
    });
  }

  /**
   * Marca como obrigatórios os campos das secções que o estado escolhido exige.
   *
   * <p>Desde 2026-09-22 nem tudo o que a secção Avaliação mostra é exigido a toda a gente: o
   * impacto e o requisito só pesam nas ocorrências que se graduam. Por isso isto também tem de
   * correr quando muda a classificação, e não apenas quando muda o estado.
   */
  private syncStateValidators(): void {
    const controls = this.form.controls;
    // Cada estado cobra a secção anterior: é passar ao tratamento que exige a análise feita, e
    // passar ao seguimento que exige o tratamento planeado. Entrar num estado não exige a
    // secção que esse estado abre — senão não se entrava nele para a preencher.
    const analysis = this.requiredFrom('EM_TRATAMENTO');
    const treatment = this.requiredFrom('EM_MONITORIZACAO');
    // A eficácia não é exigida em momento nenhum: verifica-se uns seis meses depois do fecho, e
    // quem fecha antes usa o tipo "Sujeito a verificação" (revisão de 2026-10-12). Era exigida a
    // quem fechava a partir de Em avaliação, e a ocorrência ficava aberta à espera dela.
    setRequired(controls.impact, analysis && this.graded());
    setRequired(controls.requirementReference, analysis && this.requirementRequired());
    setRequired(controls.triageResult, analysis && this.isCaseWork());
    setRequired(controls.triageDecidedAt, analysis && this.isCaseWork());
    setRequired(controls.actionImplementationDeadline, treatment);
    // O desfecho deixou de ser exclusivo das denúncias e reclamações.
    setRequired(controls.outcome, this.isEdit() && this.closing());
  }

  private watchType(): void {
    this.form.controls.type.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((type) => {
      this.type.set(type);
      // Fora da Participação/Comunicação não há natureza.
      if (type !== 'PARTICIPACAO_COMUNICACAO') this.clearNature();
      // A origem já não depende do tipo, mas o detalhe sim (a árvore da auditoria e da
      // monitorização muda com ele).
      this.revalidateOriginDetail();
      this.assumeIdentifiedOffSite();
      this.syncStateValidators();
    });
  }

  /**
   * A natureza comanda o motivo da reclamação e as categorias da denúncia: mudá-la limpa o que
   * deixou de fazer sentido. O canal (origem) deixou de depender dela em 2026-10-05.
   */
  private watchNature(): void {
    this.form.controls.nature.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((nature) => {
      this.nature.set(nature);
      if (nature !== 'RECLAMACAO') {
        this.complaintReason.set(null);
        this.form.controls.complaintReason.setValue(null, { emitEvent: false });
        this.form.controls.complaintReasonOther.reset('', { emitEvent: false });
        this.form.controls.recurringComplaint.setValue(false, { emitEvent: false });
      }
      if (nature !== 'DENUNCIA') {
        this.reportCategories.set([]);
        this.form.controls.conflictParties.reset('', { emitEvent: false });
      }
      this.enforceIdentification();
      // A origem pode já estar escolhida (a de uma derivada vem fixa) antes de o bloco do
      // participante existir: é agora que ele abre, e abre já com "Identificado".
      this.assumeIdentifiedOffSite();
      this.syncStateValidators();
    });
    this.form.controls.complaintReason.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((reason) => {
        this.complaintReason.set(reason);
        this.enforceIdentification();
      });
    this.form.controls.processArea.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((area) => this.processArea.set(area));
    this.form.controls.participantRelation.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((relation) => {
        this.participantRelation.set(relation);
        if (relation !== 'COLABORADOR') {
          this.form.controls.participantWorkerType.setValue(null, { emitEvent: false });
        }
      });
    this.form.controls.dataSubjectRelation.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((relation) => {
        this.dataSubjectRelation.set(relation);
        if (relation !== 'COLABORADOR') {
          this.form.controls.dataSubjectWorkerType.setValue(null, { emitEvent: false });
        }
      });
    this.form.controls.awaitingElements.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((waiting) => {
        this.awaitingElements.set(waiting);
        if (!waiting) {
          this.form.controls.elementsRequestedAt.reset('', { emitEvent: false });
          this.form.controls.elementsResponseDeadline.reset('', { emitEvent: false });
          return;
        }
        // O pedido assume hoje e a resposta quinze dias depois — as datas do documento, todas
        // editáveis. Quem marca a espera não tem de as escrever à mão.
        if (!this.form.controls.elementsRequestedAt.value) {
          this.form.controls.elementsRequestedAt.setValue(isoToday());
        }
      });
    this.form.controls.elementsRequestedAt.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((requestedAt) => {
        if (requestedAt) {
          this.form.controls.elementsResponseDeadline.setValue(addDays(requestedAt, 15), { emitEvent: false });
        }
      });
    this.form.controls.effectivenessEvaluation.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((evaluation) => {
        this.effectiveness.set(evaluation);
        if (evaluation !== 'NAO_FOI_POSSIVEL_AFERIR') {
          this.form.controls.effectivenessUnableReason.reset('', { emitEvent: false });
        }
      });
  }

  /**
   * Pedir os elementos em falta, ou registar que já foram pedidos (documento de 2026-10-08).
   *
   * <p>Não é campo gravado: é a pergunta que decide de onde vem a data do pedido. Em
   * <em>Pedir</em> a data é a do envio, e sai daqui um e-mail que fica nas Comunicações; em
   * <em>Já solicitado</em> escreve-se à mão a data em que foi pedido.
   */
  protected readonly elementsMode = signal<'PEDIR' | 'JA_SOLICITADO'>('PEDIR');

  protected setElementsMode(mode: 'PEDIR' | 'JA_SOLICITADO'): void {
    this.elementsMode.set(mode);
    if (mode === 'PEDIR') this.elementsEmail.set(false);
  }

  /** O compositor do pedido, aberto pelo "Enviar e-mail". */
  protected readonly elementsEmail = signal(false);
  protected readonly sendingElements = signal(false);
  protected elementsTo = '';
  protected elementsSubject = '';
  protected elementsBody = '';

  protected openElementsEmail(): void {
    this.elementsTo = this.form.controls.participantEmail.value || '';
    this.elementsSubject = 'Pedido de elementos adicionais';
    this.elementsBody = 'Para podermos avançar com a análise do seu caso, precisamos de '
      + 'elementos adicionais. Agradecemos que responda a esta mensagem com a informação '
      + 'que puder facultar.';
    this.serverError.set(null);
    this.elementsEmail.set(true);
  }

  protected cancelElementsEmail(): void {
    this.elementsEmail.set(false);
  }

  protected sendElementsEmail(): void {
    const id = this.id();
    if (id === null) return;
    if (!this.elementsTo.trim() || !this.elementsSubject.trim() || !this.elementsBody.trim()) {
      this.serverError.set('O pedido precisa de destinatário, assunto e mensagem.');
      return;
    }
    this.sendingElements.set(true);
    this.service.sendMessage(id, {
      to: this.elementsTo.trim(),
      subject: this.elementsSubject.trim(),
      body: this.elementsBody.trim(),
      audience: 'EXTERNA',
    }).subscribe({
      next: () => {
        this.sendingElements.set(false);
        this.elementsEmail.set(false);
        // A data do pedido é a do envio, e a contagem dos 15 dias parte daí.
        this.form.controls.elementsRequestedAt.setValue(this.today);
        this.form.markAsDirty();
        this.refreshStepMarks();
        this.toasts.success('Pedido enviado. Fica registado nas Comunicações.');
      },
      error: () => {
        this.sendingElements.set(false);
        this.serverError.set('Não foi possível enviar o pedido de elementos.');
      },
    });
  }

  /** Motivos que não admitem anonimato passam a identificação a Identificado. */
  private enforceIdentification(): void {
    if (this.identificationLocked() && this.form.controls.participantIdentification.value !== 'IDENTIFICADO') {
      this.form.controls.participantIdentification.setValue('IDENTIFICADO');
    }
  }

  private clearNature(): void {
    this.nature.set(null);
    this.complaintReason.set(null);
    this.reportCategories.set([]);
    this.form.controls.nature.setValue(null, { emitEvent: false });
    this.form.controls.complaintReason.setValue(null, { emitEvent: false });
    this.form.controls.complaintReasonOther.reset('', { emitEvent: false });
  }

  /** Tipo ou natureza mudaram: a origem escolhida pode já não pertencer à lista. */
  private revalidateOrigin(): void {
    const origin = this.form.controls.origin.value;
    if (!origin) return;
    if (!originsFor(this.scope(), this.subScope()).includes(origin)) {
      this.origin.set(null);
      this.form.controls.origin.setValue(null, { emitEvent: false });
      this.originDetail.set(null);
      this.form.controls.originDetail.setValue(null, { emitEvent: false });
      this.form.controls.originOther.reset('', { emitEvent: false });
    }
  }

  /** Mudar a categoria de origem limpa o detalhe e o texto. */
  private watchOrigin(): void {
    this.form.controls.origin.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((origin) => {
      if (origin === this.origin()) return;
      this.origin.set(origin);
      this.originDetail.set(null);
      this.form.controls.originDetail.setValue(null, { emitEvent: false });
      this.form.controls.originOther.reset('', { emitEvent: false });
      this.assumeIdentifiedOffSite();
    });
    this.form.controls.originDetail.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((detail) => {
      this.originDetail.set(detail);
      if (detail !== 'SATISFACAO') this.form.controls.interestedParty.setValue(null, { emitEvent: false });
      if (detail === 'ACAO_RESPOSTA_OCORRENCIA' && this.parentId() === null && !this.parentCandidates().length) {
        this.loadParentCandidates();
      }
    });
  }

  /**
   * Fora do site, a participação assume-se identificada.
   *
   * <p>Quem atende um telefonema, recebe um e-mail ou escreve a ocorrência depois de uma reunião
   * sabe com quem falou — e ficava à espera de escolher "identificado" para os campos do nome e
   * do contacto aparecerem. Passam a estar abertos de início.
   *
   * <p>O anónimo continua a poder escolher-se: uma denúncia pode chegar por carta ou por e-mail
   * sem assinatura, e o canal admite-o. O que muda é por onde o formulário começa, não o que
   * permite — tirar a opção deixava quem a recebe sem forma de a registar com a origem certa.
   */
  private assumeIdentifiedOffSite(): void {
    const origin = this.origin();
    if (origin === null || origin === 'SITE') return;
    if (!this.showParticipant()) return;
    if (this.form.controls.participantIdentification.value !== null) return;
    this.form.controls.participantIdentification.setValue('IDENTIFICADO');
  }

  private watchParticipant(): void {
    this.form.controls.participantIdentification.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((participant) => {
        this.participant.set(participant);
        if (participant === 'ANONIMO') {
          this.form.controls.participantName.reset('', { emitEvent: false });
          this.form.controls.participantEmail.reset('', { emitEvent: false });
          this.form.controls.participantClientId.reset(null, { emitEvent: false });
          // Anónima só entra pelo site (documento de 2026-10-05): se já havia outra origem
          // escolhida, deixa de servir.
          if (this.form.controls.origin.value !== null && this.form.controls.origin.value !== 'SITE') {
            this.form.controls.origin.setValue(null);
          }
        }
      });
  }

  /** Âmbito/subâmbito/sistemas mudaram: o detalhe de origem pode ter ficado inválido. */
  private revalidateOriginDetail(): void {
    const detail = this.form.controls.originDetail.value;
    if (!detail) return;
    const allowed = originDetailsFor(this.origin(), this.scope(), this.subScope(), this.systems(), this.type());
    if (!allowed.includes(detail)) {
      this.originDetail.set(null);
      this.form.controls.originDetail.setValue(null, { emitEvent: false });
      this.form.controls.originOther.reset('', { emitEvent: false });
    }
  }

  private clearSubScope(): void {
    this.subScope.set(null);
    this.systems.set([]);
    this.form.controls.subScope.setValue(null, { emitEvent: false });
    this.form.controls.subScopeOther.reset('', { emitEvent: false });
  }

  private clearType(): void {
    this.type.set(null);
    this.clearNature();
    this.form.controls.type.setValue(null, { emitEvent: false });
    this.form.controls.typeOther.reset('', { emitEvent: false });
  }

  protected toggleSystem(system: IntegratedSystem, checked: boolean): void {
    const next = checked ? [...this.systems(), system] : this.systems().filter((item) => item !== system);
    this.systems.set(next);
    const type = this.form.controls.type.value;
    if (type && !typesFor(this.scope(), this.subScope()).includes(type)) this.clearType();
    this.revalidateOriginDetail();
    this.revalidateComplaint();
  }

  protected isSystemSelected(system: IntegratedSystem): boolean {
    return this.systems().includes(system);
  }

  protected toggleIncidentNature(nature: IncidentNature, checked: boolean): void {
    const next = checked
      ? [...this.incidentNatures(), nature]
      : this.incidentNatures().filter((item) => item !== nature);
    this.incidentNatures.set(next);
  }

  protected isNatureSelected(nature: IncidentNature): boolean {
    return this.incidentNatures().includes(nature);
  }

  protected toggleReportCategory(category: ReportCategory, checked: boolean): void {
    // Sem o filtro, dois eventos a dizer "marcado" gravavam a categoria duas vezes.
    const restantes = this.reportCategories().filter((item) => item !== category);
    this.reportCategories.set(checked ? [...restantes, category] : restantes);
    // "Outra irregularidade" não admite anonimato — a escolha prende a identificação.
    this.enforceIdentification();
  }

  protected isReportCategorySelected(category: ReportCategory): boolean {
    return this.reportCategories().includes(category);
  }

  protected readonly systemsError = signal<string | null>(null);
  protected readonly categoriesError = signal<string | null>(null);
  /** Guardar falhou na validação do cliente: os campos em falta podem estar fora do ecrã. */
  protected readonly hasBlockingErrors = signal(false);

  private loadParentCandidates(): void {
    this.service.list({ year: null, size: 200 }).subscribe((page) => {
      this.parentCandidates.set(page.content
        .filter((occurrence) => occurrence.id !== this.id())
        .map((occurrence) => ({ id: occurrence.id, label: `${occurrence.code} — ${occurrence.title}` })));
    });
  }

  /**
   * A derivada nasce com o âmbito, o subâmbito e o processo/área da mãe — "devem ser iguais à mãe
   * com possibilidade de atualização" (revisão de 2026-10-19). O tipo não vem: uma não
   * conformidade dá muitas vezes origem a uma ação corretiva.
   */
  private inheritFromParent(parent: Occurrence): void {
    this.scope.set(parent.scope);
    this.subScope.set(parent.subScope);
    this.systems.set([...parent.integratedSystems]);
    this.processArea.set(parent.processArea);
    this.form.controls.scopeOther.setValidators(parent.scope === 'OUTRO' ? Validators.required : null);
    // Calado, como no patch da edição: as cascatas limpavam o que se acabou de pôr.
    this.form.patchValue({
      scope: parent.scope,
      scopeOther: parent.scopeOther ?? '',
      subScope: parent.subScope,
      subScopeOther: parent.subScopeOther ?? '',
      processArea: parent.processArea,
      processAreaOther: parent.processAreaOther ?? '',
    }, { emitEvent: false });
    this.syncStateValidators();
    this.refreshStepMarks();
  }

  /**
   * Enquanto a ligação à mãe existir a origem não muda: escolher "Auditorias" numa derivada
   * pedia um detalhe que a janela não mostra, e a gravação era recusada sem nada para corrigir
   * (revisão de 2026-10-19). O controlo desativado continua a ir no envio (`getRawValue`).
   */
  private lockDerivedOrigin(): void {
    this.form.controls.origin.disable({ emitEvent: false });
  }

  /** A ocorrência de onde a nova foi replicada — o cabeçalho di-lo, para não se confundir. */
  protected readonly replicaOf = signal<string | null>(null);

  /**
   * Começa uma ocorrência nova a partir de outra (revisão de 2026-10-13: "às vezes tenho OC
   * muito semelhantes, mas que não estão relacionadas").
   *
   * <p>Vem a classificação, a origem e a descrição — o que torna as duas parecidas. Não vem o
   * que é de cada caso: a data (fica a de hoje), a análise, o tratamento, a eficácia e o estado.
   * Nem os dados de quem participou ou do titular: são dados pessoais, e uma ocorrência
   * parecida não é a mesma pessoa. Também não fica ligada à original — para isso há a
   * ocorrência derivada.
   */
  private patchReplica(source: Occurrence): void {
    this.replicaOf.set(source.code);
    // "Site" não se escolhe à mão (revisão de 2026-10-15): a réplica de uma participação do site
    // fica com a origem por escolher, em vez de trazer uma que o servidor recusaria.
    const fromSite = source.origin === 'SITE';
    this.scope.set(source.scope);
    this.subScope.set(source.subScope);
    this.systems.set([...source.integratedSystems]);
    this.type.set(source.type);
    this.origin.set(fromSite ? null : source.origin);
    this.originDetail.set(fromSite ? null : source.originDetail);
    this.nature.set(source.nature);
    this.processArea.set(source.processArea);
    this.complaintReason.set(source.complaintReason);
    this.incidentNatures.set([...source.incidentNatures]);
    this.reportCategories.set([...source.reportCategories]);
    this.form.controls.scopeOther.setValidators(source.scope === 'OUTRO' ? Validators.required : null);
    // Calado, como no patch da edição: as cascatas limpavam o que se acabou de pôr.
    this.form.patchValue({
      title: source.title,
      description: source.description ?? '',
      scope: source.scope,
      scopeOther: source.scopeOther ?? '',
      subScope: source.subScope,
      subScopeOther: source.subScopeOther ?? '',
      type: source.type,
      typeOther: source.typeOther ?? '',
      nature: source.nature,
      processArea: source.processArea,
      processAreaOther: source.processAreaOther ?? '',
      complaintReason: source.complaintReason,
      complaintReasonOther: source.complaintReasonOther ?? '',
      reportCategoryOther: source.reportCategoryOther ?? '',
      recurringComplaint: source.recurringComplaint ?? false,
      origin: fromSite ? null : source.origin,
      originDetail: fromSite ? null : source.originDetail,
      originOther: fromSite ? '' : source.originOther ?? '',
      interestedParty: source.interestedParty,
    }, { emitEvent: false });
    this.syncStateValidators();
    this.refreshStepMarks();
  }

  private patch(occurrence: Occurrence): void {
    this.code.set(occurrence.code);
    this.originalState.set(occurrence.state);
    this.originalSituation.set(occurrence.situation);
    this.parentId.set(occurrence.parentId);
    if (occurrence.parentId) {
      this.service.get(occurrence.parentId).subscribe((parent) => this.parentCode.set(parent.code));
      // As derivadas antigas, de antes da origem fixa, guardam a que tinham — mas não a trocam.
      this.lockDerivedOrigin();
    }
    this.scope.set(occurrence.scope);
    this.subScope.set(occurrence.subScope);
    this.systems.set([...occurrence.integratedSystems]);
    this.type.set(occurrence.type);
    this.origin.set(occurrence.origin);
    this.loadedOrigin.set(occurrence.origin);
    this.originDetail.set(occurrence.originDetail);
    this.loadedOriginDetail.set(occurrence.originDetail);
    this.participant.set(occurrence.participantIdentification);
    this.nature.set(occurrence.nature);
    this.processArea.set(occurrence.processArea);
    this.complaintReason.set(occurrence.complaintReason);
    this.participantRelation.set(occurrence.participantRelation);
    this.dataSubjectRelation.set(occurrence.dataSubjectRelation);
    this.effectiveness.set(occurrence.effectivenessEvaluation);
    // Com data de pedido gravada, os elementos já foram pedidos: a pergunta abre nessa resposta.
    this.elementsMode.set(occurrence.elementsRequestedAt ? 'JA_SOLICITADO' : 'PEDIR');
    this.incidentNatures.set([...occurrence.incidentNatures]);
    this.legacyIncidentNatures.set(occurrence.subScope === 'CONFORMIDADE_PROTECAO_DADOS'
      && occurrence.incidentNatures.length > 0);
    this.reportCategories.set([...occurrence.reportCategories]);
    this.situation.set(occurrence.situation);
    this.state.set(occurrence.state);
    this.awaitingElements.set(occurrence.awaitingElements ?? false);
    this.syncStateValidators();
    this.form.controls.scopeOther.setValidators(occurrence.scope === 'OUTRO' ? Validators.required : null);
    this.form.patchValue({
      title: occurrence.title,
      description: occurrence.description ?? '',
      scope: occurrence.scope,
      scopeOther: occurrence.scopeOther ?? '',
      subScope: occurrence.subScope,
      subScopeOther: occurrence.subScopeOther ?? '',
      type: occurrence.type,
      typeOther: occurrence.typeOther ?? '',
      nature: occurrence.nature,
      processArea: occurrence.processArea,
      processAreaOther: occurrence.processAreaOther ?? '',
      complaintReason: occurrence.complaintReason,
      complaintReasonOther: occurrence.complaintReasonOther ?? '',
      reportCategoryOther: occurrence.reportCategoryOther ?? '',
      recurringComplaint: occurrence.recurringComplaint ?? false,
      origin: occurrence.origin,
      originDetail: occurrence.originDetail,
      originOther: occurrence.originOther ?? '',
      interestedParty: occurrence.interestedParty,
      participantIdentification: occurrence.participantIdentification,
      participantName: occurrence.participantName ?? '',
      participantEmail: occurrence.participantEmail ?? '',
      participantPhone: occurrence.participantPhone ?? '',
      participantConfidential: occurrence.participantConfidential ?? false,
      participantRelation: occurrence.participantRelation,
      participantWorkerType: occurrence.participantWorkerType,
      participantClientId: occurrence.participantClientId ?? null,
      dataSubjectName: occurrence.dataSubjectName ?? '',
      dataSubjectContact: occurrence.dataSubjectContact ?? '',
      dataSubjectRelation: occurrence.dataSubjectRelation,
      dataSubjectRelationOther: occurrence.dataSubjectRelationOther ?? '',
      dataSubjectWorkerType: occurrence.dataSubjectWorkerType,
      dataSubjectValidated: occurrence.dataSubjectValidated ?? false,
      targetedPersons: occurrence.targetedPersons ?? '',
      conflictParties: occurrence.conflictParties ?? '',
      occurrenceDate: occurrence.occurrenceDate,
      actionImplementationDeadline: occurrence.actionImplementationDeadline ?? '',
      effectivenessEvaluationDeadline: occurrence.effectivenessEvaluationDeadline ?? '',
      effectivenessEvaluationDate: occurrence.effectivenessEvaluationDate ?? '',
      initialAnalysis: occurrence.initialAnalysis ?? '',
      causeAnalysis: occurrence.causeAnalysis ?? '',
      impact: occurrence.impact,
      requirementReference: occurrence.requirementReference ?? '',
      actionDescription: occurrence.actionDescription ?? '',
      actionResponsibleId: occurrence.actionResponsibleId,

      effectivenessEvaluation: occurrence.effectivenessEvaluation,
      effectivenessFindings: occurrence.effectivenessFindings ?? '',
      effectivenessUnableReason: occurrence.effectivenessUnableReason ?? '',

      triageResult: occurrence.triageResult,
      triageDecidedAt: occurrence.triageDecidedAt ?? '',
      triageDecisionDeadline: occurrence.triageDecisionDeadline ?? '',
      informDeadline: occurrence.informDeadline ?? '',
      awaitingElements: occurrence.awaitingElements ?? false,
      elementsRequestedAt: occurrence.elementsRequestedAt ?? '',
      elementsResponseDeadline: occurrence.elementsResponseDeadline ?? '',
      outcome: occurrence.outcome,
      legalHold: occurrence.legalHold ?? false,
      legalHoldNote: occurrence.legalHoldNote ?? '',

      situation: occurrence.situation,
      state: occurrence.state,
      closingType: occurrence.closingType,
      closingResult: occurrence.closingResult ?? '',
      closingDate: occurrence.closingDate ?? '',
      reevaluationDeadline: occurrence.reevaluationDeadline ?? '',
    }, { emitEvent: false });
    // O patch entra calado de propósito (senão as cascatas limpavam o que se acabou de
    // carregar), mas então nada avisa a barra de progresso. Avisa-se aqui.
    this.refreshStepMarks();
  }

  protected submit(): void {
    // Um segundo clique (ou Enter) enquanto o primeiro pedido segue não grava uma segunda ocorrência.
    if (this.loading()) return;
    this.systemsError.set(null);
    this.categoriesError.set(null);
    this.refusedRequired = [];
    // O que foi recusado da vez anterior não se arrasta: corrigir e voltar a tentar tem de
    // limpar o aviso, senão fica lá a dizer o que já não é verdade.
    this.serverError.set(null);
    if (this.form.controls.state.hasError('transition')) {
      this.form.controls.state.setErrors(null);
    }
    this.markConditionalRequirements();
    // As duas regras de transição, ditas aqui para não se descobrirem pela recusa do servidor.
    const transitionError = this.transitionProblem();
    if (transitionError) {
      this.serverError.set(transitionError);
      this.form.controls.state.setErrors({ transition: true });
      this.refuse(transitionError, false);
      return;
    }
    if (this.form.invalid || this.systemsError() || this.categoriesError()) {
      this.refuse('Não foi possível guardar: há campos por preencher.');
      return;
    }
    this.hasBlockingErrors.set(false);
    this.loading.set(true);
    this.serverError.set(null);
    this.serverFieldErrors.set([]);
    const value = this.form.getRawValue();
    // Campos ocultos não são enviados: seguem como null/vazio conforme a visibilidade.
    const payload = {
      title: value.title,
      description: value.description || null,
      scope: value.scope,
      scopeOther: this.showScopeOther() ? value.scopeOther || null : null,
      subScope: this.showSubScope() ? value.subScope : null,
      subScopeOther: this.showSubScopeOther() ? value.subScopeOther || null : null,
      integratedSystems: this.showIntegratedSystems() ? this.systems() : [],
      type: value.type,
      typeOther: this.showTypeOther() ? value.typeOther || null : null,
      nature: this.showNature() ? value.nature : null,
      processArea: this.showProcessArea() ? value.processArea : null,
      processAreaOther: this.showProcessAreaOther() ? value.processAreaOther || null : null,
      complaintReason: this.isComplaint() ? value.complaintReason : null,
      complaintReasonOther: this.showComplaintOther() ? value.complaintReasonOther || null : null,
      recurringComplaint: this.isComplaint() ? value.recurringComplaint : null,
      // Um campo só no ecrã, dois destinos: a Segurança da Informação escreve nas naturezas do
      // incidente, os restantes âmbitos nas categorias.
      reportCategories: this.isReport() && !this.showIncidentNatures() ? this.reportCategories() : [],
      reportCategoryOther: this.showReportCategoryOther() ? value.reportCategoryOther || null : null,
      origin: value.origin,
      originDetail: this.showOriginDetail() ? value.originDetail : null,
      originOther: this.showOriginOther() ? value.originOther || null : null,
      interestedParty: this.showInterestedParty() ? value.interestedParty : null,
      parentId: this.parentId() ?? (this.showParentPicker() ? value.parentSelect : null),
      followUpId: this.followUpId(),
      criticalCaseId: this.isEdit() ? undefined : this.criticalCaseId(),
      participantIdentification: this.showParticipant() ? value.participantIdentification : null,
      participantName: this.showParticipantContacts() ? value.participantName || null : null,
      participantEmail: this.showParticipantContacts() ? value.participantEmail || null : null,
      participantPhone: this.showParticipantContacts() ? value.participantPhone || null : null,
      participantConfidential: this.showParticipantContacts() ? value.participantConfidential : null,
      participantRelation: this.showParticipantContacts() ? value.participantRelation : null,
      participantWorkerType: this.showWorkerType() ? value.participantWorkerType : null,
      participantClientId: this.showParticipantContacts() ? value.participantClientId ?? null : null,
      dataSubjectName: this.showDataSubject() ? value.dataSubjectName || null : null,
      dataSubjectContact: this.showDataSubject() ? value.dataSubjectContact || null : null,
      dataSubjectRelation: this.showDataSubject() ? value.dataSubjectRelation : null,
      dataSubjectRelationOther: this.showDataSubject() ? value.dataSubjectRelationOther || null : null,
      dataSubjectWorkerType: this.showDataSubjectWorkerType() ? value.dataSubjectWorkerType : null,
      dataSubjectValidated: this.showDataSubject() ? value.dataSubjectValidated : null,
      incidentNatures: this.showIncidentNatures() ? this.incidentNatures() : [],
      targetedPersons: this.showTargetedPersons() ? value.targetedPersons || null : null,
      conflictParties: this.showConflictParties() ? value.conflictParties || null : null,
      occurrenceDate: value.occurrenceDate,
      actionImplementationDeadline: value.actionImplementationDeadline || null,
      effectivenessEvaluationDeadline: value.effectivenessEvaluationDeadline || null,
      effectivenessEvaluationDate: value.effectivenessEvaluationDate || null,
      initialAnalysis: value.initialAnalysis || null,
      causeAnalysis: value.causeAnalysis || null,
      impact: value.impact,
      requirementReference: value.requirementReference || null,
      actionDescription: value.actionDescription || null,
      actionResponsibleId: value.actionResponsibleId,
      effectivenessEvaluation: value.effectivenessEvaluation,
      effectivenessFindings: value.effectivenessFindings || null,
      effectivenessUnableReason: this.showUnableReason() ? value.effectivenessUnableReason || null : null,
      // Prazo da triagem e conservação: só nas denúncias e reclamações.
      triageResult: value.triageResult,
      triageDecidedAt: value.triageDecidedAt || null,
      triageDecisionDeadline: this.isCaseWork() ? value.triageDecisionDeadline || null : null,
      informDeadline: this.hasCommunicant() ? value.informDeadline || null : null,
      // A espera de elementos é de qualquer ocorrência desde a revisão de 2026-10-19.
      awaitingElements: value.awaitingElements,
      elementsRequestedAt: value.awaitingElements ? value.elementsRequestedAt || null : null,
      elementsResponseDeadline: value.awaitingElements ? value.elementsResponseDeadline || null : null,
      legalHold: value.legalHold,
      legalHoldNote: value.legalHold ? value.legalHoldNote || null : null,
      // O registo nasce Aberto/Registado: só a edição manda estado e situação.
      ...(this.isEdit() ? {
        situation: value.situation,
        state: value.state,
        closingType: this.closing() ? value.closingType : null,
        closingResult: this.closing() ? value.closingResult.trim() || null : null,
        // O desfecho é de todos os fechos desde 2026-09-26. Preso ao isCaseWork, era descartado
        // no envio em tudo o que não fosse denúncia ou reclamação: o campo aparecia preenchido no
        // ecrã, o servidor recebia vazio e respondia "o fecho exige o desfecho".
        outcome: this.closing() ? value.outcome : null,
        closingDate: this.closing() ? value.closingDate || null : null,
        // O prazo de reavaliação saiu do ecrã (documento de 2026-10-08). Devolve-se o que estava
        // gravado: tirar o campo não é apagar o que as ocorrências antigas lá têm.
        reevaluationDeadline: value.reevaluationDeadline || null,
      } : {}),
    };
    const request = this.id()
      ? this.service.update(this.id()!, payload)
      : this.service.create(payload);
    const wasEdit = this.isEdit();
    const naDescricao = this.step() === 'descricao';
    request.subscribe({
      next: (saved) => {
        this.stateBeforeStage = null;
        this.triageBeforeStage = null;
        this.toasts.success(wasEdit ? `${saved.code} atualizada.` : `${saved.code} registada.`);
        this.warnEmptyStage(saved);
        // Concluiu uma etapa: fica no formulário, com a secção seguinte já aberta.
        const seguinte = this.stepAfterSave;
        this.stepAfterSave = null;
        if (seguinte) {
          this.loading.set(false);
          this.patch(saved);
          this.form.markAsPristine();
          this.step.set(seguinte);
          return;
        }
        // Gravada a descrição de uma ocorrência que ainda está em Registado, pergunta-se se é
        // para começar a análise — é o passo seguinte natural, e assim não se sai do fluxo para
        // o ir procurar na ficha.
        if (saved.state === 'REGISTADO' && (!wasEdit || naDescricao)) {
          this.loading.set(false);
          this.askStartAnalysis.set(saved.id);
          return;
        }
        this.router.navigate(['/', this.auth.currentTenant(), 'ocorrencias', saved.id]);
      },
      error: (error: HttpErrorResponse) => {
        this.loading.set(false);
        this.stepAfterSave = null;
        this.undoStageState();
        const body = error.error as ApiError | undefined;
        const message = body?.message ?? 'Não foi possível guardar a ocorrência.';
        this.serverError.set(message);
        this.toasts.error(message);
        this.applyServerFieldErrors(body?.fieldErrors ?? []);
      },
    });
  }

  /**
   * Pré-preenche o registo com o que o caso já diz: descrição, data de reporte e quem é o visado
   * (Colaborador ou Cliente, com contactos). A origem fica Essentials — é o canal de onde o caso
   * vem —, e o resto (âmbito, tipo…) é de quem regista decidir.
   */
  private prefillFromCriticalCase(detail: CriticalCaseDetail): void {
    this.criticalCaseTarget.set(detail.target.name);
    const reported = (detail.reportDate ?? '').slice(0, 10);
    const form = this.form.controls;
    if (detail.description) form.description.setValue(detail.description);
    if (reported) form.occurrenceDate.setValue(reported > this.today ? this.today : reported);
    form.origin.setValue('ESSENTIALS');
    form.participantIdentification.setValue('IDENTIFICADO');
    form.participantRelation.setValue(detail.target.type === 'CLIENT' ? 'CLIENTE' : 'COLABORADOR');
    form.participantName.setValue(detail.target.name);
    form.participantEmail.setValue(detail.target.email ?? '');
    form.participantPhone.setValue(detail.target.phone ?? '');
    this.refreshStepMarks();
  }

  /**
   * Leva a recusa do servidor até ao campo que a causou.
   *
   * <p>Antes só aparecia a frase de topo — "Há datas inconsistentes na ocorrência." — e ficava
   * a cargo de quem preenche descobrir qual das quatro datas estava errada. O servidor já dizia
   * qual: o que faltava era usá-lo.
   *
   * <p>Marcar o campo como tocado é o que o faz ficar vermelho; o erro é limpo assim que o
   * valor mudar, porque aí os validadores voltam a correr.
   */
  private applyServerFieldErrors(fieldErrors: readonly { field: string; message: string }[]): void {
    const messages: string[] = [];
    for (const { field, message } of fieldErrors) {
      messages.push(message);
      const control = this.form.get(field);
      if (control) {
        control.setErrors({ ...(control.errors ?? {}), server: message });
        control.markAsTouched();
      }
    }
    this.serverFieldErrors.set(messages);
    this.refreshStepMarks();
    this.focusFirstInvalid();
  }

  /**
   * Recusa de guardar: marca tudo, assinala os separadores e diz porquê onde não escape.
   *
   * <p>O aviso vivia só no rodapé do painel, que fica depois de um corpo com barra de rolagem —
   * e quando o campo em falta nem sequer está desenhado (a classificação escolhida não o abriu),
   * o salto não tinha para onde ir e carregar em "Guardar" parecia não fazer nada. O toast é o
   * que garante que a recusa se vê, venha de onde vier.
   */
  /** Os campos que a última recusa deixou por preencher, até se corrigirem. */
  private refusedRequired: string[] = [];

  private reimposeRefusedRequired(): void {
    for (const name of this.refusedRequired) {
      const control = this.form.get(name);
      if (control && !control.errors) control.setErrors({ required: true });
    }
  }

  private refuse(message: string, missingFields = true): void {
    // A etapa não avançou: o destino não pode ficar à espera do próximo "Guardar", e o estado
    // que o botão da etapa escolheu também não.
    this.stepAfterSave = null;
    // Os campos exigidos pelo estado da etapa (impacto, requisito, ...) deixam de ser exigidos
    // quando o estado volta atrás — e a recusa ficava a dizer "há campos por preencher" sem
    // nenhum campo marcado nem separador a vermelho (QA de 2026-10-29). Lembram-se antes de
    // desfazer e impõem-se de novo à mão.
    this.refusedRequired = Object.entries(this.form.controls)
      .filter(([, control]) => control.hasError('required'))
      .map(([name]) => name);
    this.undoStageState();
    this.reimposeRefusedRequired();
    this.form.markAllAsTouched();
    // A recusa de transição não é "faltam campos": dizer as duas coisas punha no rodapé um
    // aviso que contradizia o outro.
    this.hasBlockingErrors.set(missingFields);
    this.refreshStepMarks();
    this.toasts.error(message);
    this.focusFirstInvalid();
  }

  /**
   * Marca os campos que a classificação e o estado tornam obrigatórios.
   *
   * <p>Não são `Validators` do formulário: dependem do que está escolhido noutros campos, por
   * isso impõem-se à mão a cada gravação. E repõem-se depois de o separador do erro aparecer no
   * ecrã — um campo que ainda não estava pintado revalida-se ao nascer, e a revalidação apaga o
   * erro posto à mão. Sem isso, a recusa deixava o separador a vermelho sem um único campo
   * marcado, e só ao segundo "Guardar" é que se via o que faltava.
   */
  private markConditionalRequirements(): void {
    if (this.showSubScope() && this.form.controls.subScope.value === null) {
      this.form.controls.subScope.setErrors({ required: true });
    }
    if (this.showIntegratedSystems() && this.systems().length < 2) {
      this.systemsError.set('Selecione pelo menos dois sistemas aplicáveis.');
    }
    if (this.showOriginDetail() && this.form.controls.originDetail.value === null) {
      this.form.controls.originDetail.setErrors({ required: true });
    }
    if (this.showParticipant() && this.form.controls.participantIdentification.value === null) {
      this.form.controls.participantIdentification.setErrors({ required: true });
    }
    if (this.showNature() && this.form.controls.nature.value === null) {
      this.form.controls.nature.setErrors({ required: true });
    }
    if (this.isComplaint() && this.form.controls.complaintReason.value === null) {
      this.form.controls.complaintReason.setErrors({ required: true });
    }
    // O processo/área é obrigatório na reclamação, menos quando ela chegou pelo site: quem
    // reclama de fora não sabe o processo, e quem a regista à mão a partir do site também não
    // tem de saber já — a equipa completa-o na análise. É a regra que o servidor aplica.
    if (this.isComplaint() && this.showProcessArea() && this.origin() !== 'SITE'
        && this.form.controls.processArea.value === null) {
      this.form.controls.processArea.setErrors({ required: true });
    }
    if (this.isReport() && !this.showIncidentNatures() && !this.reportCategories().length) {
      this.categoriesError.set('Indique a natureza do incidente ou violação.');
    }
    if (this.showInterestedParty() && this.form.controls.interestedParty.value === null) {
      this.form.controls.interestedParty.setErrors({ required: true });
    }
    if (this.showUnableReason() && !this.form.controls.effectivenessUnableReason.value.trim()) {
      this.form.controls.effectivenessUnableReason.setErrors({ required: true });
    }
    if (this.showParentPicker() && this.form.controls.parentSelect.value === null) {
      this.form.controls.parentSelect.setErrors({ required: true });
    }
    // A Análise cumpre-se com uma das duas — nenhum Validators.required cobre "um de dois" —,
    // salvo nos casos graves, em que a causa não se dispensa.
    if (this.requiredFrom('EM_TRATAMENTO')) {
      if (this.causeRequired() && !this.form.controls.causeAnalysis.value.trim()) {
        this.form.controls.causeAnalysis.setErrors({ required: true });
      } else if (!this.form.controls.initialAnalysis.value.trim()
          && !this.form.controls.causeAnalysis.value.trim()) {
        this.form.controls.initialAnalysis.setErrors({ required: true });
      }
    }
    if (this.isEdit() && this.closing()) {
      if (this.form.controls.closingType.value === null) {
        this.form.controls.closingType.setErrors({ required: true });
      }
      if (this.closingResultRequired() && !this.form.controls.closingResult.value.trim()) {
        this.form.controls.closingResult.setErrors({ required: true });
      }
      if (this.form.controls.outcome.value === null) {
        this.form.controls.outcome.setErrors({ required: true });
      }
    }
  }

  /**
   * Leva o ecrã ao primeiro campo por corrigir e põe-lhe o foco.
   *
   * <p>O formulário tem nove secções: sem isto, um erro numa data lá em baixo deixava a pessoa
   * a olhar para um aviso no fim da página sem saber para onde ir.
   */
  private focusFirstInvalid(): void {
    setTimeout(() => {
      // Só está no ecrã o separador aberto: se o campo por corrigir vive noutro, abre-se esse
      // primeiro — senão o foco não teria para onde ir e a pessoa ficava com um aviso e um
      // formulário aparentemente correto à frente.
      const stepWithError = this.steps()
        .find((step) => (OccurrenceForm.STEP_FIELDS[step.key] ?? [])
          .some((name) => this.form.get(name)?.invalid));
      if (stepWithError && stepWithError.key !== this.step()) {
        this.step.set(stepWithError.key);
      }
      // Segundo tempo: trocar de separador troca o que está no DOM, e o campo só existe depois
      // de o Angular o pintar. Procurá-lo já aqui devolvia sempre nada.
      setTimeout(() => {
        // Os campos acabados de pintar chegam limpos: as exigências voltam a ser impostas.
        this.markConditionalRequirements();
        this.reimposeRefusedRequired();
        this.form.markAllAsTouched();
        this.refreshStepMarks();
        this.focusInvalidInCurrentStep();
      });
    });
  }

  private focusInvalidInCurrentStep(): void {
    // A ordem é a do DOM, não a da declaração do formulário: "primeiro" é o primeiro que a
    // pessoa vê. Procurar pelos elementos em vez de pelos controlos ignora de graça os campos
    // que a classificação não abriu — esses não estão no ecrã e não há para onde saltar.
    // As listas de caixas (natureza do incidente, sistemas aplicáveis) não são controlos do
    // formulário: o erro delas é o aviso que ficou escrito no grupo, e o foco vai à primeira caixa.
    const element = Array.from(
      this.host.nativeElement.querySelectorAll<HTMLElement>('[formcontrolname], input.form-check-input[type=checkbox]'),
    ).find((candidate) => candidate.hasAttribute('formcontrolname')
      ? this.form.get(candidate.getAttribute('formcontrolname') ?? '')?.invalid
      : !!candidate.closest('.col-12')?.querySelector('small.text-danger'));
    if (!element) return;
    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // O date-picker é um componente: quem recebe o foco é o botão lá dentro.
    const focusable = element.matches('input, select, textarea, button')
      ? element
      : element.querySelector<HTMLElement>('input, select, textarea, button');
    focusable?.focus({ preventScroll: true });
  }
}
